import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer, Switch, message, Modal, Table, Tooltip, Select, DatePicker, Input, InputNumber, Button, Checkbox } from 'antd';
import { InfoCircleOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getGroupProfitDetail, getGroupEventInfo, saveGroupDepartureConfirm, saveGroupScheduleConfirm, uploadS3, saveGroupInfoAttachment, getGroupOrderListByGroupNo, getPreDepositInfoBySaleNo, createPreDeposit, deletePreDeposit, getCashbookList, submitPreDepositApproval, applyPreDepositWithdrawal, deletePreDepositWithdrawalRegistration, cancelApprovalDocument, cancelSubmissionDocument, getPreDepositWithdrawalList, getSaleDepositList, createDisbursementRecord, updateGroupSettlementStatus, getReservationPriceInfo, getSupplierPartners, getOtherIncomeListBySaleNo, saveOtherIncome, deleteOtherIncome, getReceiptList01, getOtherIncomeDepositList, applyReceiptToOtherIncome, deleteOtherIncomePayment } from '../services/apiService';
import { getAuthHeader, getUserInfo } from '../utils/tokenManager';
import { getAuthInfo } from '../services/authService';
import SettlementModal from './SettlementModal';
import { getGroupManagerCheck, saveGroupManagerCheck } from '../services/groupManagerCheckApi';
import { getPreDepositRemarks, savePreDepositRemark, deletePreDepositRemark } from '../services/preDepositRemarkApi';
import * as XLSX from 'xlsx';

// GroupInfoDrawer: Reusable detailed group info drawer
// Props:
// - open: boolean
// - onClose: function
// - saleNo: number|string|null (required to fetch)
// - title: string (optional; will fallback to 행사단체명)
// - preDepositAutoOpen: boolean (optional; when true, auto-open 전도금 모달 on open)
// - settlementAutoOpen: boolean (optional; when true, auto-open 정산서 모달 after data loads)
// - directPreDepositMode: boolean (optional; when true, skip drawer and show pre-deposit modal directly)
// - onUpdated: function (optional; called after group info is saved)
const GroupInfoDrawer = ({ open, onClose, saleNo, title, preDepositAutoOpen = false, settlementAutoOpen = false, directPreDepositMode = false, onUpdated }) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [groupProfit, setGroupProfit] = React.useState(null);
  const [groupEvent, setGroupEvent] = React.useState(null);
  const [departureConfirm, setDepartureConfirm] = React.useState(false);
  const [scheduleConfirm, setScheduleConfirm] = React.useState(false);
  const [settlementClosed, setSettlementClosed] = React.useState(false);
  // 담당자 정산 확인 — ERP 가 아니라 Supabase(group_manager_check)에 단체번호별로 저장
  const [managerCheck, setManagerCheck] = React.useState(null); // { 확인여부, 확인자, 확인일시 } | null
  const [managerCheckSaving, setManagerCheckSaving] = React.useState(false);
  const [applyVatToProfit, setApplyVatToProfit] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const fileInputRef = React.useRef(null);
  // Orders by group number
  const [groupOrders, setGroupOrders] = React.useState([]);
  const [groupOrdersLoading, setGroupOrdersLoading] = React.useState(false);
  const [groupOrdersError, setGroupOrdersError] = React.useState(null);
  const [expandedOrders, setExpandedOrders] = React.useState(new Set());
  // Remark (비고) edit state
  const [remark, setRemark] = React.useState('');
  const [remarkSaving, setRemarkSaving] = React.useState(false);
  // Group name (단체명) edit state
  const [isEditingGroupName, setIsEditingGroupName] = React.useState(false);
  const [editedGroupName, setEditedGroupName] = React.useState('');
  const [groupNameSaving, setGroupNameSaving] = React.useState(false);
  const navigate = useNavigate();
  // Breakdown modal state
  const [breakdownOpen, setBreakdownOpen] = React.useState(false); // modal visibility
  const [breakdownType, setBreakdownType] = React.useState(null); // 'revenue' | 'receivable'
  // Pre-deposit modal state
  const [preDepOpen, setPreDepOpen] = React.useState(false);
  const [preDepHelpOpen, setPreDepHelpOpen] = React.useState(false);
  const [preDepLoading, setPreDepLoading] = React.useState(false);
  const [preDepError, setPreDepError] = React.useState(null);
  const [preDepRows, setPreDepRows] = React.useState([]);
  const [preDepSummary, setPreDepSummary] = React.useState(null);
  const [preDepAmount, setPreDepAmount] = React.useState('');
  const [preDepSaving, setPreDepSaving] = React.useState(false);
  const [preDepDeletingId, setPreDepDeletingId] = React.useState(null);
  const [preDepRemark, setPreDepRemark] = React.useState('');
  // 전도금 비고 수정본 (Supabase pre_deposit_remark). ERP 는 상신된 건의 비고 수정을 막아서 따로 둔다.
  const [preDepRemarkMap, setPreDepRemarkMap] = React.useState(() => new Map());
  const [preDepRemarkEdit, setPreDepRemarkEdit] = React.useState(null); // { detailNo, original, value }
  const [preDepRemarkSaving, setPreDepRemarkSaving] = React.useState(false);
  const enrichedPreDepRowsRef = React.useRef([]);
  const [supplierPickOpen, setSupplierPickOpen] = React.useState(false);
  const [supplierPickLoading, setSupplierPickLoading] = React.useState(false);
  const [supplierPickError, setSupplierPickError] = React.useState('');
  const [supplierPickRows, setSupplierPickRows] = React.useState([]);
  const [supplierPickFilter, setSupplierPickFilter] = React.useState('');
  const [selectedSupplierNo, setSelectedSupplierNo] = React.useState(null);
  const [preDepPaymentMethod, setPreDepPaymentMethod] = React.useState('무통장');
  const [preDepDate, setPreDepDate] = React.useState(null);
  const [preDepSender, setPreDepSender] = React.useState('');
  const [showAddPreDepForm, setShowAddPreDepForm] = React.useState(false);
  const [bankAccounts, setBankAccounts] = React.useState([]);
  const [bankAccountsLoading, setBankAccountsLoading] = React.useState(false);
  const [bankAccountsError, setBankAccountsError] = React.useState('');
  const [preDepAccountId, setPreDepAccountId] = React.useState('');
  // Cashbook (출납 리스트) state
  const [cashbookRows, setCashbookRows] = React.useState([]);
  const [cashbookAllRows, setCashbookAllRows] = React.useState([]);
  const [cashbookMappingRows, setCashbookMappingRows] = React.useState([]);
  const [cashbookLoading, setCashbookLoading] = React.useState(false);
  const [cashbookError, setCashbookError] = React.useState(null);
  const [preDepMappingSaving, setPreDepMappingSaving] = React.useState(false);
  const [selectedCashbookTx, setSelectedCashbookTx] = React.useState(null);
  const [cashbookRangePreset, setCashbookRangePreset] = React.useState('custom');
  const [cashbookStartDate, setCashbookStartDate] = React.useState(null);
  const [cashbookEndDate, setCashbookEndDate] = React.useState(null);
  // Other income (기타손익) modal state
  const [otherIncomeOpen, setOtherIncomeOpen] = React.useState(false);
  const [otherIncomeLoading, setOtherIncomeLoading] = React.useState(false);
  const [otherIncomeError, setOtherIncomeError] = React.useState(null);
  const [otherIncomeRows, setOtherIncomeRows] = React.useState([]);
  const [otherIncomeSaving, setOtherIncomeSaving] = React.useState(false);
  const [otherIncomeDeletingId, setOtherIncomeDeletingId] = React.useState(null);
  const [editingOtherIncomeNo, setEditingOtherIncomeNo] = React.useState(null);
  const [oiItem, setOiItem] = React.useState('');
  const [oiManager, setOiManager] = React.useState('');
  const [oiAmount, setOiAmount] = React.useState('');
  const [oiRemark, setOiRemark] = React.useState('');
  const [oiTaxInvoice, setOiTaxInvoice] = React.useState(false);
  // Other income ↔ receipt (입금) mapping
  const [oiDepositTargetNo, setOiDepositTargetNo] = React.useState(null);
  const [oiDepositList, setOiDepositList] = React.useState([]);
  const [oiDepositSummary, setOiDepositSummary] = React.useState(null);
  const [oiDepositLoading, setOiDepositLoading] = React.useState(false);
  const [oiReceiptList, setOiReceiptList] = React.useState([]);
  const [oiReceiptLoading, setOiReceiptLoading] = React.useState(false);
  const [oiReceiptRangePreset, setOiReceiptRangePreset] = React.useState('3m');
  const [oiReceiptStartDate, setOiReceiptStartDate] = React.useState(() => dayjs().subtract(3, 'month').format('YYYY-MM-DD'));
  const [oiReceiptEndDate, setOiReceiptEndDate] = React.useState(() => dayjs().format('YYYY-MM-DD'));
  const [oiSelectedReceiptTxNo, setOiSelectedReceiptTxNo] = React.useState(null);
  const [oiSelectedReceiptRow, setOiSelectedReceiptRow] = React.useState(null);
  const [oiApplyAmount, setOiApplyAmount] = React.useState(null);
  const [oiApplyNote, setOiApplyNote] = React.useState('');
  const [oiApplying, setOiApplying] = React.useState(false);
  const [oiDepositDeletingId, setOiDepositDeletingId] = React.useState(null);

  const exportJsonRowsToExcel = React.useCallback(({ rows, fileName, sheetName }) => {
    try {
      const safeRows = Array.isArray(rows) ? rows : [];
      if (safeRows.length === 0) {
        message.error('내보낼 데이터가 없습니다.');
        return;
      }
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(safeRows);
      XLSX.utils.book_append_sheet(wb, ws, sheetName || 'Sheet1');
      XLSX.writeFile(wb, fileName || 'export.xlsx');
    } catch (e) {
      try { console.error('[GroupInfoDrawer] export excel failed', e); } catch (_) {}
      message.error('엑셀 내보내기에 실패했습니다.');
    }
  }, []);

  const handleExportPreDepositExcel = React.useCallback(() => {
    // enrichedPreDepRows 는 아래에서 선언되므로 ref 로 최신 값을 읽는다 (deps 에 넣으면 TDZ)
    const current = enrichedPreDepRowsRef.current;
    const list = Array.isArray(current) ? current : [];
    const rows = list.map((r, idx) => ({
      'No': idx + 1,
      '지급액': Number(String(r?.지급액 ?? '').replace(/,/g, '')) || 0,
      '출납일': r?.매핑출납일 ?? '',
      '송금카드소유자': r?.매핑송금카드소유자 ?? '',
      '등록자': r?.등록자 ?? '',
      '비고': r?.비고 ?? '',
      '종류': r?.매핑종류 ?? '',
      '결제구분': r?.매핑결제구분 ?? '',
      '세부내역번호': r?.세부내역번호 ?? '',
    }));

    const safeSaleNo = String(saleNo ?? '').trim();
    const baseName = safeSaleNo ? `예약_${safeSaleNo}` : '예약';
    exportJsonRowsToExcel({
      rows,
      fileName: `${baseName}_전도금(지출)내역.xlsx`,
      sheetName: '전도금(지출)내역',
    });
  }, [exportJsonRowsToExcel, saleNo]);

  const handleExportCashbookExcel = React.useCallback(() => {
    const list = Array.isArray(cashbookRows) ? cashbookRows : [];
    const rows = list.map((r, idx) => ({
      'No': idx + 1,
      '출납일': r?.출납일 ?? '',
      '종류': r?.종류 ?? '',
      '결제구분': r?.결제구분 ?? '',
      '송금카드소유자': r?.송금카드소유자 ?? '',
      '출납액': Number(String(r?.출납액 ?? '').replace(/,/g, '')) || 0,
      '잔액': Number(String(r?.잔액 ?? '').replace(/,/g, '')) || 0,
      '거래일련번호': r?.거래일련번호 ?? '',
    }));

    const safeSaleNo = String(saleNo ?? '').trim();
    const baseName = safeSaleNo ? `예약_${safeSaleNo}` : '예약';
    exportJsonRowsToExcel({
      rows,
      fileName: `${baseName}_출납내역.xlsx`,
      sheetName: '출납내역',
    });
  }, [cashbookRows, exportJsonRowsToExcel, saleNo]);
  // Settlement modal state
  const [settlementOpen, setSettlementOpen] = React.useState(false);
  // Deposit list for settlement
  const [depositRows, setDepositRows] = React.useState([]);
  const [depositLoading, setDepositLoading] = React.useState(false);
  const autoOpenRef = React.useRef(false);
  const settlementAutoOpenRef = React.useRef(false);

  const fmtMoney = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const num = Math.trunc(Number(String(v).replace(/,/g, '')));
    if (!Number.isFinite(num)) return String(v);
    return num.toLocaleString('ko-KR');
  };

  const openSupplierPick = React.useCallback(() => {
    setSupplierPickOpen(true);
  }, []);

  const closeSupplierPick = React.useCallback(() => {
    setSupplierPickOpen(false);
    setSupplierPickFilter('');
    setSelectedSupplierNo(null);
    setSupplierPickError('');
  }, []);

  const filteredSupplierPickRows = React.useMemo(() => {
    const rows = Array.isArray(supplierPickRows) ? supplierPickRows : [];
    const q = String(supplierPickFilter || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = String(r?.거래처명 || '').toLowerCase();
      const no = String(r?.거래처번호 || '').toLowerCase();
      return name.includes(q) || no.includes(q);
    });
  }, [supplierPickRows, supplierPickFilter]);

  const handleApplySupplierToRemark = React.useCallback(() => {
    if (!selectedSupplierNo) {
      message.warning('공급사를 선택해주세요.');
      return;
    }
    const rows = Array.isArray(supplierPickRows) ? supplierPickRows : [];
    const found = rows.find((r) => String(r?.거래처번호 ?? '') === String(selectedSupplierNo)) || null;
    const name = found?.거래처명;
    if (!name) {
      message.warning('선택한 공급사 정보를 찾지 못했습니다.');
      return;
    }
    setPreDepRemark(String(name));
    closeSupplierPick();
  }, [selectedSupplierNo, supplierPickRows, closeSupplierPick]);

  React.useEffect(() => {
    if (!supplierPickOpen) return;
    let alive = true;
    (async () => {
      try {
        setSupplierPickLoading(true);
        setSupplierPickError('');
        const list = await getSupplierPartners();
        if (!alive) return;
        setSupplierPickRows(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!alive) return;
        setSupplierPickRows([]);
        setSupplierPickError('공급사 목록을 불러오지 못했습니다.');
      } finally {
        if (!alive) return;
        setSupplierPickLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [supplierPickOpen]);

  const normalizeCellText = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };

  const normalizeCellTextLower = (value) => normalizeCellText(value).toLowerCase();

  const compareKoreanLike = (a, b) => {
    const sa = normalizeCellText(a);
    const sb = normalizeCellText(b);
    return sa.localeCompare(sb, 'ko-KR');
  };

  const compareNumberLike = (a, b) => {
    const na = Number(String(a ?? '').replace(/,/g, ''));
    const nb = Number(String(b ?? '').replace(/,/g, ''));
    const va = Number.isFinite(na) ? na : 0;
    const vb = Number.isFinite(nb) ? nb : 0;
    return va - vb;
  };

  const compareYmdLike = (a, b) => {
    const sa = normalizeCellText(a);
    const sb = normalizeCellText(b);
    return sa.localeCompare(sb);
  };

  const buildTextColumnFilter = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8, width: 220 }}>
        <Input
          size="small"
          placeholder="검색"
          value={selectedKeys?.[0] ?? ''}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <div className="flex gap-2">
          <Button size="small" type="primary" onClick={() => confirm()} style={{ flex: 1 }}>
            적용
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters && clearFilters();
              confirm();
            }}
            style={{ flex: 1 }}
          >
            초기화
          </Button>
        </div>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      const needle = normalizeCellTextLower(value);
      const hay = record?.__norm?.[dataIndex] ?? normalizeCellTextLower(record?.[dataIndex]);
      return hay.includes(needle);
    },
  });

  const buildNumberRangeColumnFilter = (dataIndex) => {
    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        const cur = (selectedKeys?.[0] && typeof selectedKeys[0] === 'object') ? selectedKeys[0] : {};
        const minVal = cur?.min;
        const maxVal = cur?.max;
        const setRange = (next) => setSelectedKeys(next && (next.min !== undefined || next.max !== undefined) ? [next] : []);
        return (
          <div style={{ padding: 8, width: 260 }}>
            <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
              <InputNumber
                size="small"
                placeholder="최소"
                value={minVal}
                onChange={(v) => setRange({ ...cur, min: (v === null || v === undefined) ? undefined : Number(v) })}
                style={{ width: '100%' }}
                formatter={(val) => fmtMoney(typeof val === 'number' ? val : Number(val || 0))}
                parser={(val) => (val ? val.toString().replace(/,/g, '') : '')}
              />
              <span className="text-xs text-gray-500" style={{ flex: '0 0 auto' }}>~</span>
              <InputNumber
                size="small"
                placeholder="최대"
                value={maxVal}
                onChange={(v) => setRange({ ...cur, max: (v === null || v === undefined) ? undefined : Number(v) })}
                style={{ width: '100%' }}
                formatter={(val) => fmtMoney(typeof val === 'number' ? val : Number(val || 0))}
                parser={(val) => (val ? val.toString().replace(/,/g, '') : '')}
              />
            </div>
            <div className="flex gap-2">
              <Button size="small" type="primary" onClick={() => confirm()} style={{ flex: 1 }}>
                적용
              </Button>
              <Button
                size="small"
                onClick={() => {
                  clearFilters && clearFilters();
                  confirm();
                }}
                style={{ flex: 1 }}
              >
                초기화
              </Button>
            </div>
          </div>
        );
      },
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
      ),
      onFilter: (value, record) => {
        const v = record?.__num?.[dataIndex] ?? Number(String(record?.[dataIndex] ?? '').replace(/,/g, ''));
        if (!Number.isFinite(v)) return false;
        const min = (value && typeof value === 'object' && value.min !== undefined) ? Number(value.min) : undefined;
        const max = (value && typeof value === 'object' && value.max !== undefined) ? Number(value.max) : undefined;
        if (min !== undefined && v < min) return false;
        if (max !== undefined && v > max) return false;
        return true;
      },
    };
  };

  const parseMoney = (v) => {
    const raw = String(v ?? '').replace(/,/g, '').replace(/[^0-9-]/g, '');
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const toYmd = (d) => {
    try {
      if (!d) return '';
      if (typeof d === 'string') return d;
      if (d?.format) return d.format('YYYY-MM-DD');
      const js = d instanceof Date ? d : new Date(d);
      if (Number.isNaN(js.getTime())) return '';
      const y = js.getFullYear();
      const m = String(js.getMonth() + 1).padStart(2, '0');
      const day = String(js.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    } catch {
      return '';
    }
  };

  const loadCashbookByRange = React.useCallback(async ({ start, end } = {}) => {
    const safeEnd = end || dayjs();
    const safeStart = start || safeEnd.subtract(1, 'month');
    try {
      setCashbookLoading(true);
      setCashbookError(null);
      setSelectedCashbookTx(null);
      const { list: cashList, mapping: cbMapping } = await getCashbookList({
        startDate: toYmd(safeStart),
        endDate: toYmd(safeEnd),
      });
      const allRows = Array.isArray(cashList) ? cashList : [];
      const mappingRows = Array.isArray(cbMapping) ? cbMapping : [];
      setCashbookAllRows(allRows);
      setCashbookMappingRows(mappingRows);
      const filtered = allRows.filter((row) => {
        const raw = row?.잔액;
        const num = Number(String(raw ?? '').toString().replace(/,/g, ''));
        return Number.isFinite(num) && num > 0;
      });
      setCashbookRows(filtered);
    } catch (err) {
      console.error(err);
      setCashbookError(err?.message || '출납 리스트를 불러오지 못했습니다.');
      setCashbookAllRows([]);
      setCashbookMappingRows([]);
      setCashbookRows([]);
    } finally {
      setCashbookLoading(false);
    }
  }, []);

  const applyCashbookPreset = React.useCallback(async (preset) => {
    const end = dayjs();
    const start = (() => {
      if (preset === 'today') return end;
      if (preset === 'week') return end.subtract(7, 'day');
      if (preset === 'month') return end.subtract(1, 'month');
      if (preset === '3months') return end.subtract(3, 'month');
      return cashbookStartDate || end.subtract(1, 'month');
    })();

    setCashbookRangePreset(preset);
    setCashbookStartDate(start);
    setCashbookEndDate(end);
    await loadCashbookByRange({ start, end });
  }, [cashbookStartDate, loadCashbookByRange]);

  const renderCashbookRangeControls = () => {
    const btnClass = (active) => (
      active
        ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700 hover:border-blue-700'
        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
    );

    return (
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-medium text-gray-700">조회기간</span>
        <button
          type="button"
          className={`inline-flex items-center rounded border px-2 py-1 text-[11px] ${btnClass(cashbookRangePreset === 'today')}`}
          onClick={() => applyCashbookPreset('today')}
          disabled={cashbookLoading}
        >
          오늘
        </button>
        <button
          type="button"
          className={`inline-flex items-center rounded border px-2 py-1 text-[11px] ${btnClass(cashbookRangePreset === 'week')}`}
          onClick={() => applyCashbookPreset('week')}
          disabled={cashbookLoading}
        >
          일주일
        </button>
        <button
          type="button"
          className={`inline-flex items-center rounded border px-2 py-1 text-[11px] ${btnClass(cashbookRangePreset === 'month')}`}
          onClick={() => applyCashbookPreset('month')}
          disabled={cashbookLoading}
        >
          한달
        </button>
        <button
          type="button"
          className={`inline-flex items-center rounded border px-2 py-1 text-[11px] ${btnClass(cashbookRangePreset === '3months')}`}
          onClick={() => applyCashbookPreset('3months')}
          disabled={cashbookLoading}
        >
          3개월
        </button>
        <button
          type="button"
          className={`inline-flex items-center rounded border px-2 py-1 text-[11px] ${btnClass(cashbookRangePreset === 'custom')}`}
          onClick={() => applyCashbookPreset('custom')}
          disabled={cashbookLoading}
        >
          기간지정
        </button>
        {cashbookRangePreset === 'custom' && (
          <DatePicker.RangePicker
            size="small"
            allowClear={false}
            value={[cashbookStartDate, cashbookEndDate]}
            onChange={async (vals) => {
              const s = vals?.[0] || null;
              const e = vals?.[1] || null;
              setCashbookStartDate(s);
              setCashbookEndDate(e);
              if (s && e) {
                await loadCashbookByRange({ start: s, end: e });
              }
            }}
          />
        )}
      </div>
    );
  };

  // Compute inclusive day count between two YYYY-MM-DD strings.
  const daysBetweenInclusive = (startStr, endStr) => {
    const s = String(startStr || '').trim();
    const e = String(endStr || '').trim();
    if (!s || !e) return null;
    // Accept 'YYYY-MM-DD' (ignore time if present)
    const sYmd = s.includes(' ') || s.includes('T') ? s.split(/[ T]/)[0] : s;
    const eYmd = e.includes(' ') || e.includes('T') ? e.split(/[ T]/)[0] : e;
    const sParts = sYmd.split('-').map((x) => parseInt(x, 10));
    const eParts = eYmd.split('-').map((x) => parseInt(x, 10));
    if (sParts.length !== 3 || eParts.length !== 3) return null;
    const sDate = new Date(sParts[0], sParts[1] - 1, sParts[2]);
    const eDate = new Date(eParts[0], eParts[1] - 1, eParts[2]);
    if (isNaN(sDate.getTime()) || isNaN(eDate.getTime())) return null;
    const ms = eDate.getTime() - sDate.getTime();
    const days = Math.floor(ms / (1000 * 60 * 60 * 24)) + 1; // inclusive
    return days > 0 ? days : null;
  };

  const handleDeletePreDeposit = (detailNo) => {
    if (!detailNo) { message.warning('세부내역번호가 없습니다.'); return; }

    const rows = Array.isArray(preDepRows) ? preDepRows : [];
    const targetRow = rows.find((row) => Number(row?.세부내역번호) === Number(detailNo)) || null;
    const documentNo = (() => {
      if (!targetRow || targetRow.결재문서번호 == null) return null;
      const n = Number(targetRow.결재문서번호);
      return Number.isNaN(n) ? null : n;
    })();

    Modal.confirm({
      title: '전도금(지출) 삭제',
      content: '해당 전도금(지출) 내역을 삭제하시겠습니까?',
      okText: '삭제',
      cancelText: '취소',
      onOk: async () => {
        try {
          setPreDepDeletingId(detailNo);

          let paymentHistoryNo = null;
          if (documentNo != null) {
            try {
              const withdrawalList = await getPreDepositWithdrawalList({ documentNo, preDepositNo: detailNo });
              const rows2 = Array.isArray(withdrawalList) ? withdrawalList : [];
              if (rows2.length > 0) {
                const wRow = rows2.find((r) => Number(r?.전도금번호) === Number(detailNo)) || rows2[0];
                if (wRow && wRow.지불내역번호 != null) {
                  const v = Number(wRow.지불내역번호);
                  if (!Number.isNaN(v)) paymentHistoryNo = v;
                }
              }
            } catch (err) {
              console.error(err);
            }
          }

          if (paymentHistoryNo != null) {
            await deletePreDepositWithdrawalRegistration({ preDepositNo: detailNo, paymentHistoryNo });
          }

          if (documentNo != null) {
            await cancelApprovalDocument({ documentNo, opinion: '' });
            await cancelSubmissionDocument({ documentNo, opinion: '' });
          }

          await deletePreDeposit({ detailNo });
          await deletePreDepositRemark(detailNo);

          message.success('전도금(지출) 내역이 삭제되었습니다.');
          const { list, summary } = await getPreDepositInfoBySaleNo({ saleNo });
          setPreDepRows(Array.isArray(list) ? list : []);
          setPreDepSummary(summary || null);
          const profit = await getGroupProfitDetail({ saleNo }).catch(() => null);
          if (profit) setGroupProfit(profit);
        } catch (e) {
          console.error(e);
          message.error(e?.message || '전도금(지출) 삭제 중 오류가 발생했습니다.');
        } finally {
          setPreDepDeletingId(null);
        }
      },
    });
  };

  const getGroupNameForSave = (overrideName) => {
    const name = overrideName ?? groupEvent?.행사단체명 ?? groupProfit?.단체명 ?? title ?? '';
    return String(name).trim();
  };

  // Save group name (단체명)
  const handleSaveGroupName = async () => {
    try {
      if (!groupEvent?.단체번호) { message.error('단체번호가 없어 저장할 수 없습니다.'); return; }
      const trimmedName = String(editedGroupName || '').trim();
      if (!trimmedName) { message.error('단체명을 입력해주세요.'); return; }
      setGroupNameSaving(true);
      const existingFilePath = groupEvent?.첨부파일경로 || '';
      const existingFileName = groupEvent?.첨부파일 || (existingFilePath ? (existingFilePath.split('/').pop() || '') : '');
      await saveGroupInfoAttachment({
        groupNo: groupEvent.단체번호,
        groupName: trimmedName,
        managerNo: groupProfit?.수배담당번호 || '',
        startDate: groupEvent?.시작일 || '',
        endDate: groupEvent?.종료일 || '',
        attachFileName: existingFileName,
        attachFilePath: existingFilePath,
        remark: String(remark || ''),
        preDepositClose: false,
        autoJoinClose: '',
        landCompanyNo: '',
      });
      message.success('단체명이 저장되었습니다.');
      // Optimistic UI update
      setGroupEvent((prev) => (prev ? { ...prev, 행사단체명: trimmedName } : prev));
      setGroupProfit((prev) => (prev ? { ...prev, 단체명: trimmedName } : prev));
      setIsEditingGroupName(false);
      // Refresh from backend
      const [refreshedProfit, refreshedEvent] = await Promise.all([
        getGroupProfitDetail({ saleNo }).catch(() => null),
        getGroupEventInfo({ saleNo }).catch(() => null),
      ]);
      if (refreshedProfit) setGroupProfit(refreshedProfit);
      if (refreshedEvent) setGroupEvent(refreshedEvent);
      onUpdated?.();
    } catch (e) {
      console.error(e);
      message.error(e?.message || '단체명 저장 중 오류가 발생했습니다.');
    } finally {
      setGroupNameSaving(false);
    }
  };

  // Save only the remark value while preserving existing attachment fields
  const handleSaveRemark = async () => {
    try {
      if (!groupEvent?.단체번호) { message.error('단체번호가 없어 저장할 수 없습니다.'); return; }
      setRemarkSaving(true);
      const existingFilePath = groupEvent?.첨부파일경로 || '';
      const existingFileName = groupEvent?.첨부파일 || (existingFilePath ? (existingFilePath.split('/').pop() || '') : '');
      const currentRemark = String(remark || '');
      await saveGroupInfoAttachment({
        groupNo: groupEvent.단체번호,
        groupName: getGroupNameForSave(),
        managerNo: groupProfit?.수배담당번호 || '',
        startDate: groupEvent?.시작일 || '',
        endDate: groupEvent?.종료일 || '',
        attachFileName: existingFileName,
        attachFilePath: existingFilePath,
        remark: currentRemark,
        preDepositClose: false,
        autoJoinClose: '',
        landCompanyNo: '',
      });
      message.success('비고가 저장되었습니다.');
      // Optimistic UI update
      setGroupEvent((prev) => (prev ? { ...prev, 단체비고: currentRemark } : prev));
      setRemark(currentRemark);
      // Then refresh and only override if backend returns a non-empty value
      const refreshed = await getGroupEventInfo({ saleNo });
      setGroupEvent(refreshed || null);
      const refreshedRemark = refreshed && (refreshed.단체비고 ?? refreshed["단체비고"]);
      if (refreshedRemark != null && String(refreshedRemark).length > 0) {
        setRemark(String(refreshedRemark));
      }
      onUpdated?.();
    } catch (e) {
      console.error(e);
      message.error(e?.message || '비고 저장 중 오류가 발생했습니다.');
    } finally {
      setRemarkSaving(false);
    }
  };

  const handleDeleteAttachment = async () => {
    try {
      if (!groupEvent?.단체번호) { message.error('단체번호가 없어 삭제할 수 없습니다.'); return; }
      Modal.confirm({
        title: '첨부파일 삭제',
        content: '첨부파일을 삭제 하시겠습니까?',
        okText: '삭제',
        cancelText: '취소',
        onOk: async () => {
          setDeleting(true);
          try {
            await saveGroupInfoAttachment({
              groupNo: groupEvent.단체번호,
              groupName: getGroupNameForSave(),
              managerNo: groupProfit?.수배담당번호 || '',
              startDate: groupEvent?.시작일 || '',
              endDate: groupEvent?.종료일 || '',
              attachFileName: '',
              attachFilePath: '',
              remark: String(remark || ''),
              preDepositClose: false,
              autoJoinClose: '',
              landCompanyNo: '',
            });
            message.success('첨부파일이 삭제되었습니다. 다시 파일을 업로드해주세요.');
            const refreshed = await getGroupEventInfo({ saleNo });
            setGroupEvent(refreshed || null);
            // Keep remark synced after delete as well
            const refreshedRemark = (refreshed && (refreshed.단체비고 ?? refreshed["단체비고"])) || '';
            setRemark(String(refreshedRemark || remark || ''));
            onUpdated?.();
          } catch (e) {
            console.error(e);
            message.error(e?.message || '삭제 중 오류가 발생했습니다.');
          } finally {
            setDeleting(false);
          }
        },
      });
    } catch (e) {
      console.error(e);
      message.error(e?.message || '삭제 중 오류가 발생했습니다.');
    }
  };

  // Load data when opened and saleNo is available
  React.useEffect(() => {
    let active = true;
    (async () => {
      if (!open || saleNo == null) return;
      try {
        setLoading(true);
        setError(null);
        setGroupProfit(null);
        setGroupEvent(null);
        const [profit, eventInfo] = await Promise.all([
          getGroupProfitDetail({ saleNo }).catch((e) => { console.error(e); return null; }),
          getGroupEventInfo({ saleNo }).catch((e) => { console.error(e); return null; }),
        ]);
        if (!active) return;
        setGroupProfit(profit || null);
        setGroupEvent(eventInfo || null);
        setDepartureConfirm(!!eventInfo?.출발확정);
        setScheduleConfirm(!!eventInfo?.일정확정);
        setSettlementClosed(eventInfo?.정산상태 === '마감');
        // Initialize remark from fetched data
        const initialRemark = (eventInfo && (eventInfo.단체비고 ?? eventInfo["단체비고"]))
          || (profit && (profit.단체비고 ?? profit["단체비고"]))
          || '';
        setRemark(String(initialRemark || ''));
        // Fetch group order list by group number
        try {
          if (eventInfo?.단체번호) {
            setGroupOrdersLoading(true);
            setGroupOrdersError(null);
            // Include canceled data as requested: @취소여부1 = '1', @취소여부2 = '0'
            const orders = await getGroupOrderListByGroupNo({ groupNo: eventInfo.단체번호, cancel1: '1', cancel2: '0' });
            if (!active) return;
            const baseRows = Array.isArray(orders) ? orders : [];
            const getRowSaleNo = (row) => row?.판매번호 ?? row?.주문판매번호 ?? null;
            const saleNoList = Array.from(
              new Set(
                baseRows
                  .map((r) => getRowSaleNo(r))
                  .filter((v) => v != null && String(v).trim() !== '')
                  .map((v) => String(v))
              )
            );
            const priceInfoMap = new Map();
            const batchSize = 8;
            for (let i = 0; i < saleNoList.length; i += batchSize) {
              const batch = saleNoList.slice(i, i + batchSize);
              const results = await Promise.all(
                batch.map(async (saleNoStr) => {
                  try {
                    const price = await getReservationPriceInfo({ saleNo: Number(saleNoStr) });
                    return { saleNoStr, price };
                  } catch (err) {
                    console.error(err);
                    return { saleNoStr, price: null };
                  }
                })
              );
              if (!active) return;
              results.forEach(({ saleNoStr, price }) => {
                if (price) priceInfoMap.set(String(saleNoStr), price);
              });
            }
            const normalized = baseRows.map((r) => {
              const rowSaleNo = getRowSaleNo(r);
              const price = rowSaleNo != null ? priceInfoMap.get(String(rowSaleNo)) : null;
              const next = {
                ...r,
                ['상품금액']: r?.['상품금액원화'] ?? r?.['상품금액'],
                ['택스및유류']: r?.['택스및유류원화'] ?? r?.['택스및유류'],
                ['추가요금']: r?.['추가요금원화'] ?? r?.['추가요금'],
                ['고객결제가']: r?.['고객결제가원화'] ?? r?.['고객결제가'],
              };
              if (!price) return next;
              return {
                ...next,
                ['상품금액']: price?.['상품금액원화'] ?? price?.['상품금액'] ?? next['상품금액'],
                ['택스및유류']: price?.['택스및유류원화'] ?? price?.['택스및유류'] ?? next['택스및유류'],
                ['추가요금']: price?.['추가요금원화'] ?? price?.['추가요금'] ?? next['추가요금'],
                ['고객결제가']: price?.['고객결제가원화'] ?? price?.['고객결제가'] ?? next['고객결제가'],
                ['프로모션할인']: price?.['프로모션할인원화'] ?? price?.['프로모션할인'],
              };
            });
            setGroupOrders(normalized);
          } else {
            setGroupOrders([]);
          }
        } catch (e) {
          if (!active) return;
          console.error(e);
          setGroupOrdersError(e?.message || '단체 주문 정보를 불러오지 못했습니다.');
        } finally {
          if (active) setGroupOrdersLoading(false);
        }
      } catch (e) {
        if (!active) return;
        console.error(e);
        setError(e?.message || '단체 정보를 불러오지 못했습니다.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open, saleNo]);

  React.useEffect(() => {
    const groupNo = groupEvent?.단체번호;
    if (!open || !groupNo) { setManagerCheck(null); return undefined; }
    let active = true;
    getGroupManagerCheck(groupNo)
      .then((row) => { if (active) setManagerCheck(row); })
      .catch(() => { if (active) setManagerCheck(null); });
    return () => { active = false; };
  }, [open, groupEvent?.단체번호]);

  // 수배담당자 본인만 담당자 정산 확인 체크 가능 (번호 우선, 없으면 이름↔로그인아이디)
  const isAssignedManager = React.useMemo(() => {
    const { userNo } = getUserInfo() || {};
    const loginId = String(getAuthInfo()?.userName || '').trim();
    const managerNo = String(groupProfit?.수배담당번호 ?? groupEvent?.수배담당번호 ?? '').trim();
    const managerName = String(
      groupProfit?.수배담당자명 ?? groupEvent?.수배담당자명 ?? groupEvent?.담당자명 ?? ''
    ).trim();
    if (userNo != null && userNo !== '' && managerNo && String(userNo) === managerNo) return true;
    if (loginId && managerName && loginId.toLowerCase() === managerName.toLowerCase()) return true;
    return false;
  }, [groupProfit?.수배담당번호, groupProfit?.수배담당자명, groupEvent?.수배담당번호, groupEvent?.수배담당자명, groupEvent?.담당자명]);

  const handleManagerCheckChange = async (checked) => {
    if (!groupEvent?.단체번호) { message.error('단체번호가 없어 저장할 수 없습니다.'); return; }
    if (!isAssignedManager) {
      message.warning('수배담당자 본인만 담당자 정산 확인을 할 수 있습니다.');
      return;
    }
    const prev = managerCheck;
    setManagerCheck({ ...(prev || {}), 확인여부: checked });
    setManagerCheckSaving(true);
    try {
      const saved = await saveGroupManagerCheck({ groupNo: groupEvent.단체번호, checked });
      setManagerCheck(saved);
      message.success(checked ? '담당자 정산 확인으로 저장되었습니다.' : '담당자 정산 확인을 해제했습니다.');
      onUpdated?.({ type: 'managerCheck', groupNo: groupEvent.단체번호, row: saved });
    } catch (e) {
      console.error(e);
      message.error(e?.message || '담당자 정산 확인 저장 중 오류가 발생했습니다.');
      setManagerCheck(prev);
    } finally {
      setManagerCheckSaving(false);
    }
  };

  // Auto-open pre-deposit modal once per open cycle if requested
  React.useEffect(() => {
    if (open && (preDepositAutoOpen || directPreDepositMode) && !autoOpenRef.current) {
      autoOpenRef.current = true;
      openPreDepositModal();
    }
    if (!open) {
      autoOpenRef.current = false;
    }
  }, [open, preDepositAutoOpen, directPreDepositMode]);

  // Auto-open settlement modal once data is ready (단체정산 상세 → 정산서 바로가기)
  React.useEffect(() => {
    if (open && settlementAutoOpen && !loading && groupProfit && !settlementAutoOpenRef.current) {
      settlementAutoOpenRef.current = true;
      setSettlementOpen(true);
    }
    if (!open) {
      settlementAutoOpenRef.current = false;
      setSettlementOpen(false);
    }
  }, [open, settlementAutoOpen, loading, groupProfit]);

  const openPreDepositModal = async () => {
    try {
      setPreDepOpen(true);
      setPreDepLoading(true);
      setPreDepError(null);
      setCashbookLoading(true);
      setCashbookError(null);
      setSelectedCashbookTx(null);
      const defaultCashbookEnd = dayjs();
      const defaultCashbookStart = defaultCashbookEnd.subtract(1, 'month');
      setCashbookRangePreset('month');
      setCashbookStartDate(defaultCashbookStart);
      setCashbookEndDate(defaultCashbookEnd);
      setShowAddPreDepForm(false);
      setPreDepPaymentMethod('무통장');
      setPreDepDate(null);
      setPreDepSender('');
      setPreDepAccountId('');
      setPreDepRemark('');
      setPreDepAmount('');
      setBankAccountsError('');
      const { list, summary } = await getPreDepositInfoBySaleNo({ saleNo });
      setPreDepRows(Array.isArray(list) ? list : []);
      setPreDepSummary(summary || null);

      // 입금/출납 계좌 정보 및 출납 리스트 조회
      try {
        // 회사 통장 계좌 목록 로드 (table8)
        try {
          setBankAccountsLoading(true);
          const { companyNo } = getUserInfo() || {};
          const payload = {
            Table1: [
              { 컬럼명: '@업체번호', 값: String(companyNo ?? '1') },
            ],
          };
          const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.RCT_S_%ED%9A%8C%EC%82%AC%EA%B8%B0%EB%B3%B8%EC%A0%95%EB%B3%B4';
          const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify(payload),
          });
          if (!res.ok) throw new Error('failed');
          const data = await res.json();
          const list8 = Array.isArray(data?.table8) ? data.table8 : [];
          setBankAccounts(list8);
          if (list8.length > 0) {
            const first = list8[0];
            setPreDepAccountId(String(first.계좌일련번호 ?? ''));
          }
        } catch (err) {
          console.error(err);
          setBankAccounts([]);
          setBankAccountsError('입금 계좌 정보를 불러오지 못했습니다.');
        } finally {
          setBankAccountsLoading(false);
        }

        await loadCashbookByRange({ start: defaultCashbookStart, end: defaultCashbookEnd });
      } catch (e) {
        console.error(e);
        setCashbookError(e?.message || '출납 리스트를 불러오지 못했습니다.');
        setCashbookAllRows([]);
        setCashbookMappingRows([]);
      }
    } catch (e) {
      console.error(e);
      setPreDepError(e?.message || '전도금(지출) 정보를 불러오지 못했습니다.');
    } finally {
      setPreDepLoading(false);
      setCashbookLoading(false);
    }
  };

  const handleAddPreDeposit = async () => {
    try {
      const amt = Number(String(preDepAmount).replace(/[\,\s]/g, '')) || 0;
      if (amt <= 0) { message.warning('전도금(지출) 지급액을 입력해주세요.'); return; }
      if (!preDepDate) { message.warning('출납일을 선택해주세요.'); return; }
      if (!preDepAccountId) { message.warning('입금/출금 계좌를 선택해주세요.'); return; }
      if (!preDepSender.trim()) { message.warning('송금/지급자를 입력해주세요.'); return; }
      const pmCode = preDepPaymentMethod === '현금' ? '254004'
        : preDepPaymentMethod === '일반카드' ? '254005'
          : '254001';

      const account = (bankAccounts || []).find((a) => String(a?.계좌일련번호 ?? '') === String(preDepAccountId));
      if (!account) {
        message.error('선택한 계좌 정보를 찾을 수 없습니다.');
        return;
      }

      const ymd = preDepDate && preDepDate.format ? preDepDate.format('YYYY-MM-DD') : String(preDepDate || '');
      if (!ymd) {
        message.warning('유효한 출납일을 선택해주세요.');
        return;
      }

      setPreDepSaving(true);

      // 1단계: 전도금 생성 (기본 전도금 내역)
      const beforeRows = Array.isArray(preDepRows) ? preDepRows : [];
      const beforeIds = beforeRows
        .map((row) => Number(row?.세부내역번호) || 0)
        .filter((v) => Number.isFinite(v) && v > 0);
      const beforeMaxId = beforeIds.length ? Math.max(...beforeIds) : 0;

      await createPreDeposit({ groupNo: groupEvent.단체번호, amount: amt, remark: preDepRemark });

      // 2단계: 출납 신규 등록
      await createDisbursementRecord({
        transactionDate: ymd,
        amount: amt,
        paymentMethodCode: pmCode,
        accountNo: account.계좌번호,
        bankCode: account.은행,
        senderName: preDepSender.trim(),
        note: preDepRemark.trim(),
      });
      try {
        setCashbookLoading(true);
        setCashbookError(null);
        // 3단계: 전도금 목록 재조회 후 방금 생성된 세부내역번호 찾기
        const { list: afterList, summary: afterSummary } = await getPreDepositInfoBySaleNo({ saleNo });
        const afterRows = Array.isArray(afterList) ? afterList : [];
        setPreDepRows(afterRows);
        setPreDepSummary(afterSummary || null);

        let preDepositNo = null;
        afterRows.forEach((row) => {
          const id = Number(row?.세부내역번호) || 0;
          if (id > beforeMaxId && (preDepositNo == null || id < preDepositNo)) {
            preDepositNo = id;
          }
        });
        if (!preDepositNo) {
          const allIds = afterRows
            .map((row) => Number(row?.세부내역번호) || 0)
            .filter((v) => Number.isFinite(v) && v > 0);
          if (allIds.length) preDepositNo = Math.max(...allIds);
        }

        if (!preDepositNo) {
          message.error('생성된 전도금(지출) 세부내역번호를 찾을 수 없습니다.');
          return;
        }

        // 4단계: 전도금 결재 상신
        await submitPreDepositApproval({ amount: amt, detailNo: preDepositNo });

        const { list: approvedList, summary: approvedSummary } = await getPreDepositInfoBySaleNo({ saleNo });
        const approvedRows = Array.isArray(approvedList) ? approvedList : [];
        setPreDepRows(approvedRows);
        setPreDepSummary(approvedSummary || null);

        // 5단계: 결재문서번호 찾기
        let documentNo = null;
        const matchedRow = approvedRows.find((row) => Number(row?.세부내역번호) === preDepositNo);
        if (matchedRow && matchedRow.결재문서번호 != null) {
          const doc = Number(matchedRow.결재문서번호);
          if (!Number.isNaN(doc)) documentNo = doc;
        }
        if (!documentNo) {
          const anyDocRow = approvedRows.find((row) => row?.결재문서번호 != null);
          if (anyDocRow) {
            const doc = Number(anyDocRow.결재문서번호);
            if (!Number.isNaN(doc)) documentNo = doc;
          }
        }

        if (!documentNo) {
          message.error('결재문서번호를 찾을 수 없습니다. 결재 상신 결과를 확인해주세요.');
          return;
        }

        // 6단계: 출납 리스트 재조회 후 가장 최신 거래일련번호 선택
        const txDate = dayjs(ymd);
        const rangeStart = (() => {
          const s = cashbookStartDate ? dayjs(cashbookStartDate) : null;
          if (!s) return txDate;
          return s.isBefore(txDate, 'day') ? s : txDate;
        })();
        const rangeEnd = (() => {
          const e = cashbookEndDate ? dayjs(cashbookEndDate) : null;
          if (!e) return txDate;
          return e.isAfter(txDate, 'day') ? e : txDate;
        })();
        const { list: cashListReload, mapping: cbMappingReload } = await getCashbookList({
          startDate: toYmd(rangeStart),
          endDate: toYmd(rangeEnd),
        });
        const allRows = Array.isArray(cashListReload) ? cashListReload : [];
        const mappingRows = Array.isArray(cbMappingReload) ? cbMappingReload : [];
        setCashbookAllRows(allRows);
        setCashbookMappingRows(mappingRows);
        const filtered = allRows.filter((row) => {
          const raw = row?.잔액;
          const num = Number(String(raw ?? '').toString().replace(/,/g, ''));
          return Number.isFinite(num) && num > 0;
        });
        setCashbookRows(filtered);

        const sorted = [...allRows].sort((a, b) => {
          const aNo = Number(a?.거래일련번호) || 0;
          const bNo = Number(b?.거래일련번호) || 0;
          return bNo - aNo;
        });

        const txRow = sorted[0];

        if (!txRow || !txRow.거래일련번호) {
          message.error('생성된 출납 내역을 찾을 수 없습니다. 출납 리스트를 확인해주세요.');
          return;
        }

        const transactionNo = txRow.거래일련번호;

        // 7단계: 출금반영 (전도금-출납 매핑)
        await applyPreDepositWithdrawal({
          transactionNo,
          documentNo,
          preDepositNo,
          amount: amt,
          exchangeRate: 1,
        });

        // 8단계: 수익/전도금 요약 및 출납 리스트 재조회
        const profit = await getGroupProfitDetail({ saleNo }).catch(() => null);
        if (profit) setGroupProfit(profit);

        try {
          setCashbookLoading(true);
          setCashbookError(null);
          const { list: cashListReload2, mapping: cbMappingReload2 } = await getCashbookList({
            startDate: toYmd(rangeStart),
            endDate: toYmd(rangeEnd),
          });
          const allRows2 = Array.isArray(cashListReload2) ? cashListReload2 : [];
          const mappingRows2 = Array.isArray(cbMappingReload2) ? cbMappingReload2 : [];
          setCashbookAllRows(allRows2);
          setCashbookMappingRows(mappingRows2);
          const filtered2 = allRows2.filter((row) => {
            const raw = row?.잔액;
            const num = Number(String(raw ?? '').toString().replace(/,/g, ''));
            return Number.isFinite(num) && num > 0;
          });
          setCashbookRows(filtered2);
        } catch (innerErr) {
          console.error(innerErr);
          setCashbookError(innerErr?.message || '출납 리스트를 다시 불러오지 못했습니다.');
        }
      } catch (err) {
        console.error(err);
        setCashbookError(err?.message || '출납 리스트를 불러오지 못했습니다.');
        setCashbookAllRows([]);
        setCashbookMappingRows([]);
      } finally {
        setCashbookLoading(false);
      }
      setPreDepAmount('');
      setPreDepRemark('');
      setPreDepSender('');
      message.success('전도금(지출)이 출납과 함께 생성되고 전도금(지출) 전환까지 완료되었습니다.');
    } catch (e) {
      console.error(e);
      message.error(e?.message || '전도금(지출) 추가 중 오류가 발생했습니다.');
    } finally {
      setPreDepSaving(false);
    }
  };

  const handleAddPreDepositWithMapping = async () => {
    try {
      if (!groupEvent?.단체번호) { message.error('단체번호가 없어 전도금(지출)을 추가할 수 없습니다.'); return; }
      const amt = Number(String(preDepAmount).replace(/[\,\s]/g, '')) || 0;
      if (amt <= 0) { message.warning('전도금(지출) 지급액을 입력해주세요.'); return; }
      if (!selectedCashbookTx) { message.warning('출납 내역을 선택해주세요.'); return; }

      const rows = Array.isArray(cashbookRows) ? cashbookRows : [];
      const selectedRow = rows.find((row) => String(row?.거래일련번호) === String(selectedCashbookTx));
      if (!selectedRow) { message.error('선택한 출납 내역을 찾을 수 없습니다.'); return; }

      const balanceNum = parseMoney(selectedRow.잔액);
      if (balanceNum <= 0) { message.warning('선택한 출납 내역의 잔액이 없습니다.'); return; }

      const maxPayable = balanceNum;
      if (amt > maxPayable) {
        message.warning(`지급액은 최대 ${fmtMoney(maxPayable)} 원까지 입력 가능합니다.`);
        setPreDepAmount(String(maxPayable));
        return;
      }

      const beforeRows = Array.isArray(preDepRows) ? preDepRows : [];
      const beforeIds = beforeRows
        .map((row) => Number(row?.세부내역번호) || 0)
        .filter((v) => Number.isFinite(v) && v > 0);
      const beforeMaxId = beforeIds.length ? Math.max(...beforeIds) : 0;

      setPreDepMappingSaving(true);

      // 2단계: 전도금 생성
      await createPreDeposit({ groupNo: groupEvent.단체번호, amount: amt, remark: preDepRemark });

      // 전도금 목록 재조회
      const { list: afterList, summary: afterSummary } = await getPreDepositInfoBySaleNo({ saleNo });
      const afterRows = Array.isArray(afterList) ? afterList : [];
      setPreDepRows(afterRows);
      setPreDepSummary(afterSummary || null);

      // 새로 생성된 세부내역번호 추정
      let newDetailNo = null;
      afterRows.forEach((row) => {
        const id = Number(row?.세부내역번호) || 0;
        if (id > beforeMaxId && (newDetailNo == null || id < newDetailNo)) {
          newDetailNo = id;
        }
      });
      if (!newDetailNo) {
        const allIds = afterRows
          .map((row) => Number(row?.세부내역번호) || 0)
          .filter((v) => Number.isFinite(v) && v > 0);
        if (allIds.length) newDetailNo = Math.max(...allIds);
      }

      if (!newDetailNo) {
        message.error('생성된 전도금(지출) 세부내역번호를 찾을 수 없습니다.');
        return;
      }

      const preDepositNo = newDetailNo;

      // 3단계: 결제 상신 (결재문서번호 생성용)
      await submitPreDepositApproval({ amount: amt, detailNo: preDepositNo });

      // 결재문서번호 포함 정보 재조회
      const { list: approvedList, summary: approvedSummary } = await getPreDepositInfoBySaleNo({ saleNo });
      const approvedRows = Array.isArray(approvedList) ? approvedList : [];
      setPreDepRows(approvedRows);
      setPreDepSummary(approvedSummary || null);

      let documentNo = null;
      const matchedRow = approvedRows.find((row) => Number(row?.세부내역번호) === preDepositNo);
      if (matchedRow && matchedRow.결재문서번호 != null) {
        const doc = Number(matchedRow.결재문서번호);
        if (!Number.isNaN(doc)) documentNo = doc;
      }
      if (!documentNo) {
        const anyDocRow = approvedRows.find((row) => row?.결재문서번호 != null);
        if (anyDocRow) {
          const doc = Number(anyDocRow.결재문서번호);
          if (!Number.isNaN(doc)) documentNo = doc;
        }
      }

      if (!documentNo) {
        message.error('결재문서번호를 찾을 수 없습니다. 결재 상신 결과를 확인해주세요.');
        return;
      }

      // 4단계: 출금반영 (전도금번호=세부내역번호, 거래일련번호=선택한 출납 내역)
      await applyPreDepositWithdrawal({
        transactionNo: selectedCashbookTx,
        documentNo,
        preDepositNo,
        amount: amt,
        exchangeRate: 1,
      });

      // 행사 수익 요약 재조회
      const profit = await getGroupProfitDetail({ saleNo }).catch(() => null);
      if (profit) setGroupProfit(profit);

      // 출납 리스트 재조회 (잔액 반영) - 조회일(오늘)을 기준으로 1년 전부터 조회
      try {
        await loadCashbookByRange({ start: cashbookStartDate, end: cashbookEndDate });
      } catch (err) {
        console.error(err);
        setCashbookError(err?.message || '출납 리스트를 불러오지 못했습니다.');
        setCashbookAllRows([]);
        setCashbookMappingRows([]);
      } finally {
        setCashbookLoading(false);
      }

      setPreDepAmount('');
      setPreDepRemark('');
      message.success('전도금(지출) 생성, 결재 상신 및 출금반영이 완료되었습니다.');
    } catch (e) {
      console.error(e);
      message.error(e?.message || '전도금(지출) 생성/출금반영 처리 중 오류가 발생했습니다.');
    } finally {
      setPreDepMappingSaving(false);
    }
  };

  const handleClose = () => {
    // reset states on close to avoid stale toggles
    setGroupProfit(null);
    setGroupEvent(null);
    setDepartureConfirm(false);
    setScheduleConfirm(false);
    setLoading(false);
    setError(null);
    setGroupOrders([]);
    setGroupOrdersLoading(false);
    setGroupOrdersError(null);
    setRemark('');
    setRemarkSaving(false);
    setPreDepOpen(false);
    // Reset group name edit state
    setIsEditingGroupName(false);
    setEditedGroupName('');
    setGroupNameSaving(false);
    onClose?.();
  };


  // Placeholder for direct mode - actual implementation moved after hooks
  const renderDirectPreDepositModal = () => {
    if (!directPreDepositMode) return null;
    return (
      <>
        {/* Pre-Deposit Help Modal */}
        <Modal
          open={preDepHelpOpen}
          onCancel={() => setPreDepHelpOpen(false)}
          footer={null}
          centered
          width={680}
          zIndex={2000}
          title="행사 전도금(지출) 내역 도움말"
        >
          <div className="space-y-4 text-xs text-gray-700">
            <div>
              <div className="font-semibold mb-1">1. 전도금(지출)이란?</div>
              <p className="mb-0">
                전도금(지출)은 단체 행사를 위해 미리 지출되는 비용(지상비, 항공료, 가이드비, 호텔비 등)을 의미합니다.
                전도금(지출)을 등록하면 정산서 작성 시 비용이 자동으로 반영되고, 행사예상수익 계산에도 포함됩니다.
              </p>
            </div>
            <div>
              <div className="font-semibold mb-1">2. 전도금(지출)을 등록하면 어떤 효과가 있나요?</div>
              <ul className="list-disc pl-4 space-y-1">
                <li>정산서에서 행사 비용인 전도금(지출)이 자동 집계되어 정산 작업이 간편해집니다.</li>
                <li>행사예상수익 = 행사매출액 - 행사미수금 - 행사전도금(지출) + 기타손익 으로 계산됩니다.</li>
                <li>부가세 반영 시 예상부가세 = 행사예상수익 ÷ 11, 세후예상수익 = 행사예상수익 - 예상부가세 입니다.</li>
                <li>전도금(지출)을 꾸준히 입력하면 행사별 실제 수익 구조를 한눈에 파악할 수 있습니다.</li>
              </ul>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2">
              <div className="font-semibold text-[11px]">3. 전도금(지출) 등록 방식 선택</div>
              <p className="mb-0">출납 내역에서 전도금(지출)을 전환하거나, "내역 추가"로 신규 출납과 함께 전도금(지출)을 등록할 수 있습니다.</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>
                  <span className="font-semibold">[신규 전도금(지출) 추가]</span>
                  <br />
                  새 출납(출금) 내역을 생성하면서 동시에 전도금(지출)을 등록합니다.
                  <br />
                  - 실제로 새로운 지출이 발생했을 때 사용합니다.
                  <br />
                  - 출금일, 지급방법, 출금 계좌, 송금 받는자, 지급액, 비고를 입력하면 출납 + 전도금(지출) + 결재 상신 + 출금반영까지 한 번에 처리됩니다.
                </li>
                <li>
                  <span className="font-semibold">[기존 출납에서 전도금(지출) 전환]</span>
                  <br />
                  이미 등록되어 있는 출납(출금) 내역의 잔액을 전도금(지출)으로 전환합니다.
                  <br />
                  - 카드 결제/계좌이체 등 출납이 먼저 잡혀 있는 경우 사용합니다.
                  <br />
                  - 하단 표에서 출납 내역을 선택하고, 전도금(지출)으로 사용할 금액을 입력한 뒤 전도금(지출)으로 전환하면, 해당 출납 잔액 범위 내에서 전도금(지출)이 생성되고 행사 비용에 반영됩니다.
                </li>
              </ul>
            </div>
            <div className="border-t pt-3 mt-2 text-[11px] text-gray-500 space-y-1">
              <div className="font-semibold">요약</div>
              <p className="mb-0">- 전도금(지출)은 단체 행사에 미리 사용하는 비용이며, 정산서와 예상수익 계산에 직접 영향을 줍니다.</p>
              <p className="mb-0">- 새로운 지출이면 "내역 추가"로 신규 출납+전도금(지출)을 등록하고, 이미 출납에 잡힌 금액이면 출납 내역을 선택해 전도금(지출)으로 전환하세요.</p>
            </div>
          </div>
        </Modal>
        {/* Pre-Deposit Modal (direct mode) */}
        <Modal
          open={open && preDepOpen}
          onCancel={handleClose}
          onOk={handleClose}
          width="min(1200px, calc(100vw - 32px))"
          title={(
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">행사 전도금(지출) 내역</span>
                  <Tooltip title="행사 전도금(지출)의 의미와 등록 방법 안내">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreDepHelpOpen(true);
                      }}
                      className="inline-flex items-center justify-center rounded-full border border-transparent text-xs text-sky-700 hover:text-sky-900 hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-1"
                      style={{ width: 22, height: 22 }}
                    >
                      <InfoCircleOutlined style={{ fontSize: 14 }} />
                    </button>
                  </Tooltip>
                </div>
                {preDepSummary && (
                  <div className="px-6 flex-1 text-[11px] text-gray-600 flex flex-wrap justify-end gap-x-4 gap-y-0.5">
                    <span>
                      단체: <span className="font-medium">{preDepSummary.행사단체명}</span>
                    </span>
                    <span>
                      수배담당: <span className="font-medium">{preDepSummary.수배담당}</span>
                    </span>
                    <span>
                      여행객: <span className="font-medium">{preDepSummary.여행객정보}</span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          okText="닫기"
          cancelButtonProps={{ style: { display: 'none' } }}
          bodyStyle={{ paddingTop: 8 }}
        >
          {preDepLoading && (
            <div className="text-gray-500 text-sm">전도금(지출) 정보를 불러오는 중...</div>
          )}
          {!preDepLoading && preDepError && (
            <div className="text-red-600 text-sm">{preDepError}</div>
          )}
          {!preDepLoading && !preDepError && (
            <>
              {/* 상단: 전도금 내역 카드 */}
              <div className="mb-3 rounded-xl border border-gray-200 bg-white px-4 py-3.5 shadow-sm">
                <div className="mb-1 flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-800">전도금(지출) 내역</div>
                  <Button size="small" onClick={handleExportPreDepositExcel}>엑셀 다운로드</Button>
                </div>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="key"
                  rowClassName={() => 'group'}
                  columns={preDepListColumns}
                  dataSource={preDepTableData}
                  scroll={{ x: 1080, y: 360 }}
                />
              </div>

              {/* 출납 내역 + 내역 추가(신규 출납+전도금) */}
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3.5 shadow-sm">
                <div className="mb-2 flex items-center justify-between border-b pb-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-800">출납 내역</div>
                    <div className="text-[11px] text-gray-500">잔액이 있는 건만 표시됩니다. 출납 내역을 선택하면 전도금(지출)으로 전환할 수 있습니다.</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="small" onClick={handleExportCashbookExcel}>엑셀 다운로드</Button>
                    <button
                      type="button"
                      className="inline-flex items-center rounded border border-blue-200 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 px-3 py-1.5 text-xs"
                      onClick={() => {
                        setShowAddPreDepForm((prev) => {
                          const next = !prev;
                          if (next) {
                            setPreDepPaymentMethod('무통장');
                            setPreDepSender('');
                            setPreDepDate(null);
                            setPreDepRemark('');
                            setPreDepAmount('');
                          }
                          return next;
                        });
                      }}
                    >
                      {showAddPreDepForm ? '취소' : '내역 추가'}
                    </button>
                  </div>
                </div>

                {showAddPreDepForm && (
                  <div className="mb-3 p-4 rounded-lg bg-gray-100">
                    <div className="text-xs font-semibold text-gray-800 mb-3">새 전도금(지출) 등록</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-[11px] font-medium text-gray-700">지급방법</span>
                          <select
                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs"
                            value={preDepPaymentMethod}
                            onChange={(e) => setPreDepPaymentMethod(e.target.value)}
                          >
                            <option value="무통장">무통장</option>
                            <option value="현금">현금</option>
                            <option value="일반카드">일반카드</option>
                          </select>
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-[11px] font-medium text-gray-700">출금일</span>
                          <DatePicker
                            size="small"
                            className="w-full py-1.5"
                            value={preDepDate}
                            onChange={(d) => setPreDepDate(d)}
                            format="YYYY-MM-DD"
                            placeholder="출납일 선택"
                          />
                        </div>
                      </div>

                      <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-[11px] font-medium text-gray-700">송금 받는자</span>
                          <Input
                            size="small"
                            className="w-full py-1.5"
                            placeholder="송금/지급자 이름"
                            value={preDepSender}
                            onChange={(e) => setPreDepSender(e.target.value)}
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-[11px] font-medium text-gray-700">출금 계좌</span>
                          <Select
                            size="small"
                            className="w-full"
                            value={preDepAccountId || undefined}
                            placeholder={bankAccountsLoading ? '계좌 불러오는 중...' : '계좌 선택'}
                            loading={bankAccountsLoading}
                            onChange={(v) => setPreDepAccountId(v)}
                            options={(bankAccounts || []).map((acc) => ({
                              value: String(acc.계좌일련번호 ?? ''),
                              label: `${acc.은행명 ?? ''} / ${acc.계좌번호 ?? ''}`,
                            }))}
                          />
                          {bankAccountsError && (
                            <span className="text-[11px] text-red-600">{bankAccountsError}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-medium text-gray-700">지급액</span>
                        <input
                          type="text"
                          className="w-40 rounded border border-gray-300 px-2 py-1 text-sm text-left tabular-nums disabled:bg-gray-100 disabled:text-gray-400"
                          placeholder={'지급액 입력'}
                          value={preDepAmount}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const cleaned = raw.replace(/[^0-9]/g, '');
                            if (!cleaned) {
                              setPreDepAmount('');
                              return;
                            }
                            let num = Number(cleaned);
                            if (!Number.isFinite(num)) {
                              setPreDepAmount(raw);
                              return;
                            }
                            setPreDepAmount(num.toLocaleString('ko-KR'));
                          }}
                        />
                      </div>

                      <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                        <span className="text-[11px] font-medium text-gray-700">비고</span>
                        <input
                          type="text"
                          className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                          placeholder="전도금(지출) 관련 메모를 입력하세요"
                          value={preDepRemark}
                          onChange={(e) => setPreDepRemark(e.target.value)}
                        />
                        <button
                          type="button"
                          className="inline-flex items-center rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 px-2.5 py-1.5 text-xs"
                          onClick={openSupplierPick}
                        >
                          공급사 선택
                        </button>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center rounded border border-blue-200 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 px-3 py-1.5 text-xs"
                          onClick={handleAddPreDeposit}
                          disabled={preDepSaving || preDepMappingSaving}
                        >
                          {preDepSaving ? '추가 중...' : '저장'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {cashbookLoading && <div className="text-xs text-gray-500">출납 리스트 불러오는 중...</div>}
                {!cashbookLoading && cashbookError && <div className="text-xs text-red-600">{cashbookError}</div>}
                {!cashbookLoading && !cashbookError && (
                  <>
                    {renderCashbookRangeControls()}
                    {(cashbookRows || []).length === 0 ? (
                      <div className="text-xs text-gray-500">잔액이 있는 출납 내역이 없습니다.</div>
                    ) : (
                      <Table
                        size="small"
                        pagination={false}
                        rowKey="key"
                        rowSelection={{
                          type: 'radio',
                          selectedRowKeys: selectedCashbookTx ? [String(selectedCashbookTx)] : [],
                          onChange: (keys) => {
                            const first = keys?.[0];
                            setSelectedCashbookTx(first != null ? String(first) : null);
                          },
                        }}
                        columns={cashbookListColumns}
                        dataSource={cashbookTableData}
                        scroll={{ x: 660, y: 260 }}
                      />
                    )}

                    <div className="mt-2 flex flex-wrap items-center gap-3 pt-2 border-t border-dashed border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[11px] font-medium text-gray-700">지급액</span>
                          {selectedCashbookRow && (
                            <span className="text-[10px] text-gray-500">
                              선택 출납 잔액:{' '}
                              <span className="tabular-nums font-semibold text-gray-700">{fmtMoney(selectedCashbookRow.잔액)}</span>
                              <span className="ml-0.5">원</span>
                            </span>
                          )}
                        </div>
                        <input
                          type="text"
                          className="w-40 rounded border border-gray-300 px-2 py-1 text-sm text-left tabular-nums disabled:bg-gray-100 disabled:text-gray-400"
                          placeholder={
                            selectedCashbookRow
                              ? '전도금(지출)으로 전환할 금액'
                              : '출납 내역 선택'
                          }
                          value={preDepAmount}
                          disabled={!selectedCashbookRow}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const cleaned = raw.replace(/[^0-9]/g, '');
                            if (!cleaned) {
                              setPreDepAmount('');
                              return;
                            }
                            let num = Number(cleaned);
                            if (!Number.isFinite(num)) {
                              setPreDepAmount(raw);
                              return;
                            }
                            setPreDepAmount(num.toLocaleString('ko-KR'));
                          }}
                        />
                      </div>

                      <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                        <span className="text-[11px] font-medium text-gray-700">비고</span>
                        <input
                          type="text"
                          className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                          placeholder="전도금(지출) 관련 메모를 입력하세요"
                          value={preDepRemark}
                          onChange={(e) => setPreDepRemark(e.target.value)}
                        />
                        <button
                          type="button"
                          className="inline-flex items-center rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 px-2.5 py-1.5 text-xs"
                          onClick={openSupplierPick}
                        >
                          공급사 선택
                        </button>
                      </div>

                      <button
                        type="button"
                        className="inline-flex items-center rounded border border-blue-200 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 px-3 py-1.5 text-xs"
                        onClick={handleAddPreDepositWithMapping}
                        disabled={preDepMappingSaving || !selectedCashbookTx}
                      >
                        {preDepMappingSaving ? '처리 중...' : '선택 출납에서 전도금(지출) 전환'}
                      </button>
                    </div>

                  </>
                )}
              </div>
            </>
          )}
        </Modal>

        <Modal
          title="공급사 선택"
          open={supplierPickOpen}
          onCancel={closeSupplierPick}
          onOk={handleApplySupplierToRemark}
          okText="선택"
          cancelText="취소"
          width={720}
          destroyOnClose
        >
          <div className="flex flex-col gap-2">
            <Input
              size="small"
              placeholder="공급사명 검색"
              value={supplierPickFilter}
              onChange={(e) => setSupplierPickFilter(e.target.value)}
            />
            {supplierPickError && (
              <div className="text-xs text-red-600">{supplierPickError}</div>
            )}
            <Table
              size="small"
              loading={supplierPickLoading}
              pagination={false}
              rowKey={(row) => String(row?.거래처번호 ?? '')}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: selectedSupplierNo ? [String(selectedSupplierNo)] : [],
                onChange: (keys) => {
                  const first = keys?.[0];
                  setSelectedSupplierNo(first != null ? String(first) : null);
                },
              }}
              onRow={(row) => ({
                onDoubleClick: () => {
                  const no = row?.거래처번호;
                  if (no == null) return;
                  const name = row?.거래처명;
                  if (!name) return;
                  setSelectedSupplierNo(String(no));
                  setPreDepRemark(String(name));
                  closeSupplierPick();
                },
              })}
              columns={[
                {
                  title: '거래처명',
                  dataIndex: '거래처명',
                  key: '거래처명',
                  render: (v) => v || '-',
                },
                {
                  title: '대표전화',
                  dataIndex: '대표전화',
                  key: '대표전화',
                  width: 160,
                  align: 'center',
                  render: (v) => v || '-',
                },
              ]}
              dataSource={filteredSupplierPickRows}
              scroll={{ y: 320 }}
            />
          </div>
        </Modal>
      </>
    );
  };

  const resolvedTitle = groupProfit?.단체명 || title || groupEvent?.행사단체명 || '단체정보';
  // Determine whether to enable vertical scroll for tables to avoid showing a scrollbar gutter when not needed
  const ordersCount = Array.isArray(groupOrders) ? groupOrders.length : 0;
  const shouldScrollYMain = ordersCount > 6; // ~6 rows fit into 260px when size="small"
  const shouldScrollYModal = ordersCount > 9; // ~9 rows fit into 360px when size="small"

  // Sort orders so that canceled (취소여부 === '1') appear at the bottom
  const sortedGroupOrders = React.useMemo(() => {
    const list = Array.isArray(groupOrders) ? [...groupOrders] : [];
    return list.sort((a, b) => {
      const ac = String(a?.취소여부 ?? '').trim() === '1' ? 1 : 0;
      const bc = String(b?.취소여부 ?? '').trim() === '1' ? 1 : 0;
      return ac - bc; // 0 before 1 => canceled rows at bottom
    });
  }, [groupOrders]);

  // Prepare mapped revenue rows for SettlementModal
  const settlementRevenueRows = React.useMemo(() => {
    const rows = Array.isArray(sortedGroupOrders) ? sortedGroupOrders : [];
    // 매출액 상세 내역에서는 취소된 주문(취소여부 === '1')은 제외
    const filtered = rows.filter((r) => String(r?.취소여부 ?? '').trim() !== '1');
    const toNum = (v) => {
      const raw = String(v ?? '').replace(/,/g, '').replace(/[^0-9-]/g, '');
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    };
    return filtered.map((r) => {
      const prod = toNum(r?.['상품금액']);
      const add = toNum(r?.['추가요금']);
      const fuel = toNum(r?.['택스및유류']);
      const pay = toNum(r?.['고객결제가']);
      const promoRaw = r?.['프로모션할인'];
      const promo = promoRaw != null && String(promoRaw).trim() !== '' ? toNum(promoRaw) : (prod + add + fuel) - pay;
      return {
        ...r,
        ['추가서비스']: r?.['추가요금'],
        ['프로모션할인']: promo,
      };
    });
  }, [sortedGroupOrders]);

  // Prepare receivable rows for SettlementModal (exclude canceled and zero-balance rows)
  const settlementReceivableRows = React.useMemo(() => {
    const rows = Array.isArray(sortedGroupOrders) ? sortedGroupOrders : [];
    const toNum = (v) => {
      const raw = String(v ?? '').replace(/,/g, '').replace(/[^0-9-]/g, '');
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    };
    return rows.filter((r) => {
      const canceled = String(r?.취소여부 ?? '').trim() === '1';
      if (canceled) return false;
      const due = toNum(r?.['미수액']);
      return due !== 0;
    });
  }, [sortedGroupOrders]);

  const selectedCashbookRow = React.useMemo(() => {
    if (!selectedCashbookTx) return null;
    const rows = Array.isArray(cashbookRows) ? cashbookRows : [];
    return rows.find((row) => String(row?.거래일련번호) === String(selectedCashbookTx)) || null;
  }, [cashbookRows, selectedCashbookTx]);

  // 전도금 목록이 바뀔 때마다 Supabase 의 수정 비고를 읽어 온다
  const preDepDetailKey = React.useMemo(() => (
    (Array.isArray(preDepRows) ? preDepRows : [])
      .map((r) => Number(r?.세부내역번호) || 0)
      .filter(Boolean)
      .sort((a, b) => a - b)
      .join(',')
  ), [preDepRows]);

  React.useEffect(() => {
    let cancelled = false;
    const nos = preDepDetailKey ? preDepDetailKey.split(',').map(Number) : [];
    if (!nos.length) { setPreDepRemarkMap(new Map()); return undefined; }
    getPreDepositRemarks(nos)
      .then((map) => { if (!cancelled) setPreDepRemarkMap(map); })
      .catch(() => { if (!cancelled) setPreDepRemarkMap(new Map()); });
    return () => { cancelled = true; };
  }, [preDepDetailKey]);

  const handleSavePreDepRemark = React.useCallback(async () => {
    const edit = preDepRemarkEdit;
    if (!edit?.detailNo) return;
    setPreDepRemarkSaving(true);
    try {
      const saved = await savePreDepositRemark({
        detailNo: edit.detailNo,
        groupNo: groupEvent?.단체번호,
        remark: edit.value,
        originalRemark: edit.original,
      });
      setPreDepRemarkMap((prev) => {
        const next = new Map(prev);
        next.set(Number(edit.detailNo), saved);
        return next;
      });
      message.success('비고가 수정되었습니다. (TRP 화면에만 반영, ERP 원본은 그대로)');
      setPreDepRemarkEdit(null);
    } catch (e) {
      message.error(e?.message || '비고 저장 중 오류가 발생했습니다.');
    } finally {
      setPreDepRemarkSaving(false);
    }
  }, [preDepRemarkEdit, groupEvent]);

  const enrichedPreDepRows = React.useMemo(() => {
    const baseRows = Array.isArray(preDepRows) ? preDepRows : [];
    // 수정 비고가 있으면 비고를 바꿔 끼우고, ERP 값은 원래비고로 남긴다.
    // (목록·엑셀·정산 PDF 모두 이 배열을 쓰므로 여기서 한 번만 바꾼다)
    const rows = preDepRemarkMap.size
      ? baseRows.map((row) => {
        const ov = preDepRemarkMap.get(Number(row?.세부내역번호) || 0);
        if (!ov) return row;
        return { ...row, 비고: ov.비고 ?? '', 원래비고: row?.비고 ?? '', 비고수정됨: true, 비고수정자: ov.수정자 || '' };
      })
      : baseRows;
    const allCash = Array.isArray(cashbookAllRows) ? cashbookAllRows : [];
    const mappings = Array.isArray(cashbookMappingRows) ? cashbookMappingRows : [];

    if (!rows.length || !mappings.length || !allCash.length) return rows;

    return rows.map((row) => {
      const detailNo = Number(row?.세부내역번호) || 0;
      if (!detailNo) return row;

      const mappingRow = mappings.find((m) => {
        const mPreId = Number(m?.전도금번호 != null ? m.전도금번호 : m?.세부내역번호) || 0;
        return mPreId === detailNo;
      });
      if (!mappingRow) return row;

      const txNo = mappingRow?.거래일련번호;
      const cbRow = txNo
        ? allCash.find((c) => String(c?.거래일련번호) === String(txNo))
        : null;
      if (!cbRow) return row;

      return {
        ...row,
        매핑출납일: cbRow.출납일,
        매핑종류: cbRow.종류,
        매핑결제구분: cbRow.결제구분,
        매핑송금카드소유자: cbRow.송금카드소유자,
        매핑계좌카드번호: cbRow.계좌카드번호,
      };
    });
  }, [preDepRows, preDepRemarkMap, cashbookAllRows, cashbookMappingRows]);
  enrichedPreDepRowsRef.current = enrichedPreDepRows;

  const preDepTableData = React.useMemo(() => {
    const list = Array.isArray(enrichedPreDepRows) ? enrichedPreDepRows : [];
    return list.map((r, idx) => ({
      key: String(r?.세부내역번호 || idx),
      ...r,
      __norm: {
        매핑출납일: normalizeCellTextLower(r?.매핑출납일),
        매핑송금카드소유자: normalizeCellTextLower(r?.매핑송금카드소유자),
        등록자: normalizeCellTextLower(r?.등록자),
        비고: normalizeCellTextLower(r?.비고),
        매핑종류: normalizeCellTextLower(r?.매핑종류),
        매핑결제구분: normalizeCellTextLower(r?.매핑결제구분),
      },
      __num: {
        지급액: Number(String(r?.지급액 ?? '').replace(/,/g, '')),
      },
    }));
  }, [enrichedPreDepRows]);

  const preDepListColumns = React.useMemo(() => [
    {
      title: <div style={{ textAlign: 'center' }}>지급액</div>,
      dataIndex: '지급액',
      key: '지급액',
      align: 'right',
      width: 140,
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
      sorter: (a, b) => compareNumberLike(a?.지급액, b?.지급액),
      ...buildNumberRangeColumnFilter('지급액'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>출납일</div>,
      dataIndex: '매핑출납일',
      key: '매핑출납일',
      align: 'center',
      width: 120,
      render: (v) => v || '-',
      sorter: (a, b) => compareYmdLike(a?.매핑출납일, b?.매핑출납일),
      defaultSortOrder: 'descend',
      ...buildTextColumnFilter('매핑출납일'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>송금카드소유자</div>,
      dataIndex: '매핑송금카드소유자',
      key: '매핑카드소유자',
      align: 'center',
      width: 160,
      render: (v) => v || '-',
      sorter: (a, b) => compareKoreanLike(a?.매핑송금카드소유자, b?.매핑송금카드소유자),
      ...buildTextColumnFilter('매핑송금카드소유자'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>등록자</div>,
      dataIndex: '등록자',
      key: '등록자',
      align: 'center',
      width: 140,
      sorter: (a, b) => compareKoreanLike(a?.등록자, b?.등록자),
      ...buildTextColumnFilter('등록자'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>비고</div>,
      dataIndex: '비고',
      key: '비고',
      align: 'left',
      width: 200,
      // 말줄임은 render 안에서 직접 처리 (연필 아이콘이 잘리지 않도록)
      render: (v, row) => {
        const text = v ? String(v) : '';
        const id = row?.세부내역번호;
        const tip = row?.비고수정됨
          ? `ERP 원래 비고: ${row?.원래비고 || '(없음)'}${row?.비고수정자 ? `\n수정자: ${row.비고수정자}` : ''}`
          : text;
        return (
          <div className="flex min-w-0 items-center gap-1">
            <Tooltip title={tip ? <span style={{ whiteSpace: 'pre-wrap' }}>{tip}</span> : null}>
              <span className="min-w-0 flex-1 truncate">
                {row?.비고수정됨 && (
                  <span className="mr-1 rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-700">수정됨</span>
                )}
                {text || '-'}
              </span>
            </Tooltip>
            {id ? (
              <Tooltip title="비고 수정">
                <button
                  type="button"
                  aria-label="비고 수정"
                  className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-blue-50 hover:text-blue-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreDepRemarkEdit({
                      detailNo: id,
                      original: row?.비고수정됨 ? (row?.원래비고 ?? '') : (row?.비고 ?? ''),
                      value: text,
                    });
                  }}
                >
                  <EditOutlined style={{ fontSize: 12 }} />
                </button>
              </Tooltip>
            ) : null}
          </div>
        );
      },
      sorter: (a, b) => compareKoreanLike(a?.비고, b?.비고),
      ...buildTextColumnFilter('비고'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>종류</div>,
      dataIndex: '매핑종류',
      key: '매핑종류',
      align: 'center',
      width: 120,
      render: (v) => v || '-',
      sorter: (a, b) => compareKoreanLike(a?.매핑종류, b?.매핑종류),
      ...buildTextColumnFilter('매핑종류'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>결제구분</div>,
      dataIndex: '매핑결제구분',
      key: '매핑결제구분',
      align: 'center',
      width: 120,
      render: (v) => v || '-',
      sorter: (a, b) => compareKoreanLike(a?.매핑결제구분, b?.매핑결제구분),
      ...buildTextColumnFilter('매핑결제구분'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>작업</div>,
      key: 'actions',
      align: 'center',
      width: 80,
      render: (_, row) => {
        const id = row?.세부내역번호;
        const deleting = preDepDeletingId != null && Number(preDepDeletingId) === Number(id);
        return (
          <button
            type="button"
            className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
            onClick={() => handleDeletePreDeposit(id)}
            disabled={deleting}
          >
            {deleting ? '삭제 중...' : '삭제'}
          </button>
        );
      },
    },
  ], [preDepDeletingId]);

  const cashbookTableData = React.useMemo(() => {
    const list = Array.isArray(cashbookRows) ? cashbookRows : [];
    return list.map((r, idx) => {
      const txNo = r?.거래일련번호;
      const key = (txNo !== null && txNo !== undefined && String(txNo).trim() !== '') ? String(txNo) : `idx-${idx}`;
      return {
        key,
        ...r,
        __norm: {
          출납일: normalizeCellTextLower(r?.출납일),
          종류: normalizeCellTextLower(r?.종류),
          결제구분: normalizeCellTextLower(r?.결제구분),
          송금카드소유자: normalizeCellTextLower(r?.송금카드소유자),
        },
        __num: {
          출납액: Number(String(r?.출납액 ?? '').replace(/,/g, '')),
          잔액: Number(String(r?.잔액 ?? '').replace(/,/g, '')),
        },
      };
    });
  }, [cashbookRows]);

  const cashbookListColumns = React.useMemo(() => [
    {
      title: <div style={{ textAlign: 'center' }}>출납일</div>,
      dataIndex: '출납일',
      key: '출납일',
      align: 'center',
      width: 120,
      render: (v) => v || '-',
      sorter: (a, b) => compareYmdLike(a?.출납일, b?.출납일),
      defaultSortOrder: 'descend',
      ...buildTextColumnFilter('출납일'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>종류</div>,
      dataIndex: '종류',
      key: '종류',
      align: 'center',
      width: 120,
      render: (v) => v || '-',
      sorter: (a, b) => compareKoreanLike(a?.종류, b?.종류),
      ...buildTextColumnFilter('종류'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>결제구분</div>,
      dataIndex: '결제구분',
      key: '결제구분',
      align: 'center',
      width: 120,
      render: (v) => v || '-',
      sorter: (a, b) => compareKoreanLike(a?.결제구분, b?.결제구분),
      ...buildTextColumnFilter('결제구분'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>송금카드소유자</div>,
      dataIndex: '송금카드소유자',
      key: '송금카드소유자',
      align: 'center',
      width: 160,
      render: (v) => v || '-',
      sorter: (a, b) => compareKoreanLike(a?.송금카드소유자, b?.송금카드소유자),
      ...buildTextColumnFilter('송금카드소유자'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>출납액</div>,
      dataIndex: '출납액',
      key: '출납액',
      align: 'right',
      width: 140,
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
      sorter: (a, b) => compareNumberLike(a?.출납액, b?.출납액),
      ...buildNumberRangeColumnFilter('출납액'),
    },
    {
      title: <div style={{ textAlign: 'center' }}>잔액</div>,
      dataIndex: '잔액',
      key: '잔액',
      align: 'right',
      width: 140,
      render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
      sorter: (a, b) => compareNumberLike(a?.잔액, b?.잔액),
      ...buildNumberRangeColumnFilter('잔액'),
    },
  ], []);

  const expectedProfitNum = React.useMemo(() => {
    if (!groupProfit) return 0;
    const revenue = parseMoney(groupProfit?.행사매출액);
    const receivable = parseMoney(groupProfit?.행사미수금);
    const preDeposit = parseMoney(groupProfit?.행사전도금);
    const otherIncome = parseMoney(groupProfit?.기타수익);
    return revenue - receivable - preDeposit + otherIncome;
  }, [groupProfit]);

  const resetOtherIncomeForm = React.useCallback(() => {
    setEditingOtherIncomeNo(null);
    setOiItem('');
    setOiManager(groupProfit?.수배담당자명 || '');
    setOiAmount('');
    setOiRemark('');
    setOiTaxInvoice(false);
  }, [groupProfit?.수배담당자명]);

  const resetOiDepositPanel = React.useCallback(() => {
    setOiDepositTargetNo(null);
    setOiDepositList([]);
    setOiDepositSummary(null);
    setOiDepositLoading(false);
    setOiReceiptList([]);
    setOiReceiptLoading(false);
    setOiSelectedReceiptTxNo(null);
    setOiSelectedReceiptRow(null);
    setOiApplyAmount(null);
    setOiApplyNote('');
    setOiApplying(false);
    setOiDepositDeletingId(null);
  }, []);

  const refreshOtherIncomeRows = React.useCallback(async () => {
    const list = await getOtherIncomeListBySaleNo({ saleNo }).catch(() => []);
    const nextRows = Array.isArray(list) ? list : [];
    setOtherIncomeRows(nextRows);
    const profit = await getGroupProfitDetail({ saleNo }).catch(() => null);
    if (profit) setGroupProfit(profit);
    return nextRows;
  }, [saleNo]);

  const loadOiReceiptList = React.useCallback(async (rangeOverride) => {
    if (!saleNo) return;
    setOiReceiptLoading(true);
    try {
      const startDate = rangeOverride?.startDate || oiReceiptStartDate;
      const endDate = rangeOverride?.endDate || oiReceiptEndDate;
      const receipts = await getReceiptList01({ saleNo, startDate, endDate });
      const filtered = (Array.isArray(receipts) ? receipts : []).filter((r) => Number(r?.잔액) !== 0);
      setOiReceiptList(filtered);
      setOiSelectedReceiptTxNo(null);
      setOiSelectedReceiptRow(null);
      setOiApplyAmount(null);
    } catch (e) {
      console.error(e);
      setOiReceiptList([]);
      message.error(e?.message || '수납 내역을 불러오지 못했습니다.');
    } finally {
      setOiReceiptLoading(false);
    }
  }, [saleNo, oiReceiptStartDate, oiReceiptEndDate]);

  const loadOiDepositPanel = React.useCallback(async (otherIncomeNo, rangeOverride) => {
    if (otherIncomeNo == null) return;
    setOiDepositLoading(true);
    try {
      const res = await getOtherIncomeDepositList({ otherIncomeNo });
      setOiDepositList(Array.isArray(res?.list) ? res.list : []);
      setOiDepositSummary(res?.summary || null);
    } catch (e) {
      console.error(e);
      setOiDepositList([]);
      setOiDepositSummary(null);
      message.error(e?.message || '기타손익 입금 내역을 불러오지 못했습니다.');
    } finally {
      setOiDepositLoading(false);
    }
    await loadOiReceiptList(rangeOverride);
  }, [loadOiReceiptList]);

  const getOtherIncomeAmount = React.useCallback((row) => {
    if (!row) return 0;
    return Number(String(row?.한화환산 ?? row?.금액 ?? '').toString().replace(/,/g, '')) || 0;
  }, []);

  const openOiDepositPanel = async (row) => {
    const otherIncomeNo = row?.기타수익번호;
    if (otherIncomeNo == null) return;
    setOiDepositTargetNo(otherIncomeNo);
    setOiDepositList([]);
    setOiDepositSummary(null);
    setOiSelectedReceiptTxNo(null);
    setOiSelectedReceiptRow(null);
    setOiApplyAmount(null);
    setOiApplyNote('');
    // 마이너스(손실)는 출금 매핑 미지원 → 안내만 표시
    if (getOtherIncomeAmount(row) < 0) {
      return;
    }
    await loadOiDepositPanel(otherIncomeNo);
  };

  const applyOiReceiptRangePreset = async (preset) => {
    setOiReceiptRangePreset(preset);
    if (preset === 'custom') return;
    const end = dayjs().format('YYYY-MM-DD');
    let start = end;
    if (preset === 'today') start = end;
    else if (preset === '1w') start = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
    else if (preset === '1m') start = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
    else if (preset === '3m') start = dayjs().subtract(3, 'month').format('YYYY-MM-DD');
    setOiReceiptStartDate(start);
    setOiReceiptEndDate(end);
    if (oiDepositTargetNo != null) {
      await loadOiReceiptList({ startDate: start, endDate: end });
    }
  };

  const oiDepositTargetRow = React.useMemo(() => {
    if (oiDepositTargetNo == null) return null;
    return (otherIncomeRows || []).find((r) => Number(r?.기타수익번호) === Number(oiDepositTargetNo)) || null;
  }, [otherIncomeRows, oiDepositTargetNo]);

  const oiTargetIsNegative = React.useMemo(() => {
    return getOtherIncomeAmount(oiDepositTargetRow) < 0;
  }, [oiDepositTargetRow, getOtherIncomeAmount]);

  const oiFormAmountNum = React.useMemo(() => {
    if (oiAmount === '' || oiAmount == null) return null;
    const n = Number(String(oiAmount).replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  }, [oiAmount]);

  const oiDepositDue = React.useMemo(() => {
    if (oiDepositSummary && oiDepositSummary.미수금 != null) {
      return Math.max(0, Number(oiDepositSummary.미수금) || 0);
    }
    if (!oiDepositTargetRow) return 0;
    const total = getOtherIncomeAmount(oiDepositTargetRow);
    const paid = Number(String(oiDepositTargetRow?.입금액 ?? '').toString().replace(/,/g, '')) || 0;
    return Math.max(0, total - paid);
  }, [oiDepositSummary, oiDepositTargetRow, getOtherIncomeAmount]);

  const openOtherIncomeModal = async () => {
    try {
      setOtherIncomeOpen(true);
      setOtherIncomeLoading(true);
      setOtherIncomeError(null);
      resetOtherIncomeForm();
      resetOiDepositPanel();
      const list = await getOtherIncomeListBySaleNo({ saleNo });
      setOtherIncomeRows(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setOtherIncomeError(e?.message || '기타손익 정보를 불러오지 못했습니다.');
      setOtherIncomeRows([]);
    } finally {
      setOtherIncomeLoading(false);
    }
  };

  const handleEditOtherIncome = (row) => {
    if (!row) return;
    setEditingOtherIncomeNo(row?.기타수익번호 ?? null);
    setOiItem(String(row?.항목 ?? ''));
    setOiManager(String(row?.담당자 ?? ''));
    setOiAmount(String(row?.금액 ?? ''));
    setOiRemark(String(row?.비고 ?? ''));
    setOiTaxInvoice(!!row?.세금계산서여부);
  };

  const handleSaveOtherIncome = async () => {
    try {
      if (!groupEvent?.단체번호) {
        message.error('단체번호가 없어 기타손익을 저장할 수 없습니다.');
        return;
      }
      const item = String(oiItem || '').trim();
      if (!item) {
        message.warning('항목을 입력해주세요.');
        return;
      }
      const amt = Number(String(oiAmount).replace(/[\,\s]/g, '')) || 0;
      if (amt === 0) {
        message.warning('금액을 입력해주세요.');
        return;
      }
      setOtherIncomeSaving(true);
      const isEdit = editingOtherIncomeNo != null && editingOtherIncomeNo !== '';
      const beforeCount = Array.isArray(otherIncomeRows) ? otherIncomeRows.length : 0;
      await saveOtherIncome({
        otherIncomeNo: isEdit ? editingOtherIncomeNo : null,
        groupNo: groupEvent.단체번호,
        item,
        manager: String(oiManager || '').trim(),
        currencyCode: '058001',
        amount: amt,
        exchangeRate: 1,
        remark: oiRemark,
        taxInvoice: oiTaxInvoice,
      });
      const nextRows = await refreshOtherIncomeRows();

      if (!isEdit && nextRows.length <= beforeCount) {
        message.error('저장 요청은 성공했지만 기타손익이 반영되지 않았습니다. 다시 시도해주세요.');
        return;
      }

      message.success(isEdit ? '기타손익이 수정되었습니다.' : '기타손익이 추가되었습니다.');
      resetOtherIncomeForm();
      if (oiDepositTargetNo != null) {
        const target = nextRows.find((r) => Number(r?.기타수익번호) === Number(oiDepositTargetNo));
        if (target && getOtherIncomeAmount(target) < 0) {
          setOiDepositList([]);
          setOiDepositSummary(null);
          setOiSelectedReceiptTxNo(null);
          setOiSelectedReceiptRow(null);
          setOiApplyAmount(null);
        } else if (target) {
          await loadOiDepositPanel(oiDepositTargetNo);
        } else {
          resetOiDepositPanel();
        }
      }
      onUpdated?.();
    } catch (e) {
      console.error(e);
      message.error(e?.message || '기타손익 저장 중 오류가 발생했습니다.');
    } finally {
      setOtherIncomeSaving(false);
    }
  };

  const handleDeleteOtherIncome = (row) => {
    const otherIncomeNo = row?.기타수익번호;
    if (otherIncomeNo == null) return;
    Modal.confirm({
      title: '기타손익 삭제',
      content: `"${row?.항목 || '해당 항목'}" 기타손익을 삭제하시겠습니까?`,
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          setOtherIncomeDeletingId(otherIncomeNo);
          await deleteOtherIncome({ otherIncomeNo });
          message.success('기타손익이 삭제되었습니다.');
          if (Number(editingOtherIncomeNo) === Number(otherIncomeNo)) {
            resetOtherIncomeForm();
          }
          if (Number(oiDepositTargetNo) === Number(otherIncomeNo)) {
            resetOiDepositPanel();
          }
          await refreshOtherIncomeRows();
          onUpdated?.();
        } catch (e) {
          console.error(e);
          message.error(e?.message || '기타손익 삭제 중 오류가 발생했습니다.');
        } finally {
          setOtherIncomeDeletingId(null);
        }
      },
    });
  };

  const handleApplyOiReceipt = async () => {
    try {
      if (oiDepositTargetNo == null) {
        message.error('입금 적용할 기타손익을 선택해주세요.');
        return;
      }
      if (!oiSelectedReceiptTxNo) {
        message.error('수납 내역을 선택해주세요.');
        return;
      }
      const maxByBalance = Number(oiSelectedReceiptRow?.잔액) || 0;
      const maxAllowed = Math.max(0, Math.min(maxByBalance, oiDepositDue));
      const amt = Number(oiApplyAmount) || 0;
      if (amt <= 0) {
        message.error('적용할 금액을 입력하세요.');
        return;
      }
      if (amt > maxAllowed) {
        message.error('최대 적용 가능 금액을 초과했습니다.');
        return;
      }
      setOiApplying(true);
      const hide = message.loading('입금 적용 중...', 0);
      try {
        await applyReceiptToOtherIncome({
          otherIncomeNo: oiDepositTargetNo,
          transactionNo: oiSelectedReceiptTxNo,
          amount: amt,
          note: oiApplyNote,
        });
        hide();
        message.success('기타손익에 입금이 반영되었습니다.');
        await refreshOtherIncomeRows();
        await loadOiDepositPanel(oiDepositTargetNo);
        onUpdated?.();
      } catch (e) {
        hide();
        throw e;
      }
    } catch (e) {
      console.error(e);
      message.error(e?.message || '입금 적용에 실패했습니다.');
    } finally {
      setOiApplying(false);
    }
  };

  const handleDeleteOiPayment = (paymentRow) => {
    const paymentNo = paymentRow?.결제번호;
    if (paymentNo == null) return;
    Modal.confirm({
      title: '입금 삭제',
      content: '선택한 기타손익 입금 내역을 삭제하시겠습니까?',
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          setOiDepositDeletingId(paymentNo);
          await deleteOtherIncomePayment({ paymentNo });
          message.success('입금 내역이 삭제되었습니다.');
          await refreshOtherIncomeRows();
          if (oiDepositTargetNo != null) {
            await loadOiDepositPanel(oiDepositTargetNo);
          }
          onUpdated?.();
        } catch (e) {
          console.error(e);
          message.error(e?.message || '입금 삭제에 실패했습니다.');
        } finally {
          setOiDepositDeletingId(null);
        }
      },
    });
  };

  // 부가세 포함 금액에서 10% 부가세 분리 (예상수익 / 11)
  const expectedVatNum = React.useMemo(() => Math.trunc(Number(expectedProfitNum) / 11) || 0, [expectedProfitNum]);
  const afterTaxProfitNum = React.useMemo(() => Number(expectedProfitNum) - expectedVatNum, [expectedProfitNum, expectedVatNum]);

  // Load deposit list when settlement modal opens
  React.useEffect(() => {
    let active = true;
    (async () => {
      if (!settlementOpen || saleNo == null) return;
      try {
        setDepositLoading(true);
        const res = await getSaleDepositList({ saleNo });
        if (!active) return;
        setDepositRows(Array.isArray(res?.list) ? res.list : []);
      } catch (e) {
        if (!active) return;
        console.error(e);
        setDepositRows([]);
      } finally {
        if (active) setDepositLoading(false);
      }
    })();
    return () => { active = false; };
  }, [settlementOpen, saleNo]);

  // Load other-income list when settlement modal opens (for settlement document breakdown)
  React.useEffect(() => {
    let active = true;
    (async () => {
      if (!settlementOpen || saleNo == null) return;
      try {
        const list = await getOtherIncomeListBySaleNo({ saleNo });
        if (!active) return;
        setOtherIncomeRows(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!active) return;
        console.error(e);
      }
    })();
    return () => { active = false; };
  }, [settlementOpen, saleNo]);

  // Ensure pre-deposit (전도금) and cashbook mapping data are loaded when settlement modal opens,
  // so that the settlement document always reflects the latest 전도금/입금내역 even if the
  // 전도금 편집 모달 has not been opened in this session.
  React.useEffect(() => {
    let active = true;
    (async () => {
      if (!settlementOpen || saleNo == null) return;
      try {
        // 전도금 기본 목록 및 요약
        const { list, summary } = await getPreDepositInfoBySaleNo({ saleNo });
        if (!active) return;
        setPreDepRows(Array.isArray(list) ? list : []);
        setPreDepSummary(summary || null);

        // 출납 리스트 및 매핑 정보 (정산서 전도금 상세 섹션에서 사용)
        try {
          const { list: cashList, mapping: cbMapping } = await getCashbookList();
          if (!active) return;
          const allRows = Array.isArray(cashList) ? cashList : [];
          const mappingRows = Array.isArray(cbMapping) ? cbMapping : [];
          setCashbookAllRows(allRows);
          setCashbookMappingRows(mappingRows);
        } catch (err) {
          if (!active) return;
          console.error(err);
          setCashbookAllRows([]);
          setCashbookMappingRows([]);
        }
      } catch (e) {
        if (!active) return;
        console.error(e);
        setPreDepRows([]);
        setPreDepSummary(null);
      }
    })();
    return () => { active = false; };
  }, [settlementOpen, saleNo]);

  const handleUpload = async (incomingFile = null) => {
    try {
      const fileToUpload = incomingFile;
      if (!fileToUpload) { message.warning('업로드할 파일을 선택해주세요.'); return; }
      if (!groupEvent?.단체번호) { message.error('단체번호가 없어 업로드할 수 없습니다.'); return; }
      setUploading(true);
      // Build FilePath using company 1 path and EventGroup with group number as per spec
      const filePath = `1/EventGroup/${groupEvent.단체번호}/`;
      // Keep original filename; backend may append timestamp server-side
      const fileName = fileToUpload.name;
      // 1) Upload binary to S3 endpoint
      const up = await uploadS3({ file: fileToUpload, fileName, filePath });
      const uploadedUrl = up?.url || '';
      if (!uploadedUrl) throw new Error('업로드 응답에 url이 없습니다.');
      // Derive stored path (strip domain root prefix)
      const prefix = 'https://kr.object.iwinv.kr/orgin/';
      const storedPath = uploadedUrl.startsWith(prefix) ? uploadedUrl.slice(prefix.length) : `${filePath}${fileName}`;
      // 2) Save attachment into group info SP
      await saveGroupInfoAttachment({
        groupNo: groupEvent.단체번호,
        groupName: getGroupNameForSave(),
        managerNo: groupProfit?.수배담당번호 || '',
        startDate: groupEvent?.시작일 || '',
        endDate: groupEvent?.종료일 || '',
        attachFileName: fileName,
        attachFilePath: storedPath,
        remark: String(remark || ''),
        preDepositClose: false,
        autoJoinClose: '',
        landCompanyNo: '',
      });
      message.success('첨부파일이 저장되었습니다.');
      // refresh info to reflect new attachment
      const refreshed = await getGroupEventInfo({ saleNo });
      setGroupEvent(refreshed || null);
      // Keep remark synced
      const refreshedRemark = (refreshed && (refreshed.단체비고 ?? refreshed["단체비고"])) || '';
      setRemark(String(refreshedRemark || remark || ''));
      onUpdated?.();
    } catch (e) {
      console.error(e);
      message.error(e?.message || '업로드/저장 중 오류가 발생했습니다.');
    } finally {
      setUploading(false);
    }
  };

  // 전도금 비고 수정 (Supabase 에 저장, ERP 원본은 그대로).
  // direct 모드(예약상세에서 바로 연 전도금 창)와 일반 Drawer 모드 양쪽에서 렌더한다.
  const renderPreDepRemarkModal = () => (
    <Modal
      open={!!preDepRemarkEdit}
      title="전도금 비고 수정"
      onCancel={() => { if (!preDepRemarkSaving) setPreDepRemarkEdit(null); }}
      onOk={handleSavePreDepRemark}
      okText="저장"
      cancelText="취소"
      confirmLoading={preDepRemarkSaving}
      zIndex={2100}
      destroyOnClose
    >
      <div className="space-y-2">
        <div className="rounded bg-gray-50 px-3 py-2 text-xs text-gray-600">
          ERP 원래 비고: <span className="text-gray-800">{preDepRemarkEdit?.original || '(없음)'}</span>
        </div>
        <Input.TextArea
          value={preDepRemarkEdit?.value ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            setPreDepRemarkEdit((prev) => (prev ? { ...prev, value } : prev));
          }}
          autoSize={{ minRows: 3, maxRows: 8 }}
          placeholder="비고를 입력하세요"
          autoFocus
        />
        <div className="text-[11px] text-gray-500">
          고친 비고는 TRP 화면(전도금 목록·엑셀·정산 PDF)에만 보입니다. ERP 화면과 결재문서에는 원래 비고가 그대로 남습니다.
        </div>
      </div>
    </Modal>
  );

  // In directPreDepositMode, only render the pre-deposit modal (no Drawer)
  if (directPreDepositMode) {
    return (
      <>
        {renderDirectPreDepositModal()}
        {renderPreDepRemarkModal()}
      </>
    );
  }

  return (
    <>
    <Drawer
      open={open}
      onClose={handleClose}
      width={760}
      rootClassName="noto-sans-kr"
      zIndex={1000}
      title={
        <div className="min-w-0">
          {isEditingGroupName ? (
            <div className="flex items-center gap-2">
              <Input
                value={editedGroupName}
                onChange={(e) => setEditedGroupName(e.target.value)}
                onPressEnter={handleSaveGroupName}
                placeholder="단체명 입력"
                className="flex-1 text-base font-semibold"
                style={{ maxWidth: 400 }}
                autoFocus
                disabled={groupNameSaving}
              />
              <button
                onClick={handleSaveGroupName}
                disabled={groupNameSaving}
                className="px-2.5 py-1 text-xs font-medium text-white bg-blue-500 hover:bg-blue-600 rounded disabled:opacity-50"
              >
                {groupNameSaving ? '저장중...' : '저장'}
              </button>
              <button
                onClick={() => {
                  setIsEditingGroupName(false);
                  setEditedGroupName(groupProfit?.단체명 || '');
                }}
                disabled={groupNameSaving}
                className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50"
              >
                취소
              </button>
            </div>
          ) : (
            <div
              className="text-base font-semibold text-gray-900 truncate cursor-pointer hover:text-blue-600 group flex items-center gap-1.5"
              title={`${resolvedTitle} (클릭하여 수정)`}
              onClick={() => {
                setEditedGroupName(groupProfit?.단체명 || '');
                setIsEditingGroupName(true);
              }}
            >
              {resolvedTitle}
              <svg className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
          )}
          <div className="mt-0.5 text-xs text-gray-600">
            단체번호: <span className="font-medium">{groupEvent?.단체번호 ?? '-'}</span>

          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Group orders + remark + attachment (단체 주문정보) */}
        <div>
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
            <div className="w-1 h-5 bg-blue-500 rounded-full"></div>
            <div className="text-sm font-semibold text-gray-800">단체 주문 리스트</div>
          </div>
          {groupOrdersLoading && (
            <div className="text-gray-500 text-sm">주문 리스트를 불러오는 중...</div>
          )}
          {!groupOrdersLoading && groupOrdersError && (
            <div className="text-red-600 text-sm">{groupOrdersError}</div>
          )}
          {!groupOrdersLoading && !groupOrdersError && (
            <div className="pl-3 space-y-2">
              {(sortedGroupOrders || []).map((row, idx) => {
                const key = String(row?.주문번호 || idx);
                const canceled = String(row?.취소여부 ?? '').trim() === '1';
                const dueNum = parseMoney(row?.['미수액']);
                const statusStr = String(row?.입금상태 ?? '').trim();
                const unpaid = (statusStr.includes('미납')) || (Number.isFinite(dueNum) && dueNum > 0);
                const expanded = expandedOrders.has(key);
                const toggle = () => setExpandedOrders((prev) => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key); else next.add(key);
                  return next;
                });
                return (
                  <div key={key} className={`rounded-lg border ${canceled ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'} overflow-hidden`}>
                    {/* Header */}
                    <button type="button" className={`w-full flex items-center justify-between px-3 py-2 text-left ${canceled ? 'text-red-700' : 'text-gray-800'}`} onClick={toggle}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-gray-100 text-[10px] text-gray-600">{idx + 1}</span>
                        <div className="truncate">
                          <div className="flex items-center gap-2 truncate">
                            {unpaid && <span className="inline-flex items-center rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">미납</span>}
                            <span className="font-medium truncate">{row?.주문자 || '-'}</span>
                            {canceled && <span className="text-[11px]">(취소)</span>}
                          </div>
                          <div className="text-[11px] text-gray-500 truncate">주문번호: {row?.주문번호 ?? '-'}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-[12px]">
                          <span className="text-gray-500 mr-1">모객</span>
                          <span className="font-semibold">{row?.모객수량 ?? '-'}</span>
                          <span className="ml-0.5 text-gray-400">명</span>
                        </div>
                        <div className="text-[12px]">
                          <span className="text-gray-500 mr-1">고객결제가</span>
                          <span className="tabular-nums font-semibold text-blue-600">{fmtMoney(row?.['고객결제가'])}</span>
                        </div>
                        <svg className={`w-4 h-4 ${expanded ? 'rotate-180' : ''} transition-transform text-gray-500`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.27a.75.75 0 01-.02-1.06z" clipRule="evenodd"/></svg>
                      </div>
                    </button>
                    {/* Body */}
                    {expanded && (
                      <div className="px-3 pb-3 pt-1 text-sm">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                          <div className="text-gray-600">주문자휴대폰</div>
                          <div className="font-medium">{row?.주문자휴대폰 || '-'}</div>
                          <div className="text-gray-600">주문자이메일</div>
                          <div className="font-medium truncate">{row?.주문자이메일 || '-'}</div>
                          <div className="text-gray-600">대리점</div>
                          <div className="font-medium">{row?.대리점 || '-'}</div>
                          <div className="text-gray-600">모객수량</div>
                          <div className="font-medium">{row?.모객수량 ?? '-'}</div>
                          <div className="text-gray-600">상품금액</div>
                          <div className="font-medium tabular-nums">{fmtMoney(row?.['상품금액'])}</div>
                          <div className="text-gray-600">택스및유류</div>
                          <div className="font-medium tabular-nums">{fmtMoney(row?.['택스및유류'])}</div>
                          <div className="text-gray-600">입금액</div>
                          <div className="font-medium tabular-nums">{fmtMoney(row?.['입금액'])}</div>
                          <div className="text-gray-600">미수액</div>
                          <div className={`font-semibold tabular-nums ${dueNum > 0 ? 'text-red-600' : 'text-gray-800'}`}>{fmtMoney(row?.['미수액'])}</div>
                          <div className="text-gray-600">입금상태</div>
                          <div className="font-medium">{row?.입금상태 || '-'}</div>
                        </div>
                        <div className="mt-2 flex justify-end">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              const orderNo = row?.주문번호;
                              if (!orderNo) { message.warning('주문번호가 없습니다.'); return; }
                              const nextSaleNo = row?.판매번호 ?? row?.주문판매번호 ?? saleNo;
                              const productName = row?.판매상품명 ?? row?.상품명 ?? undefined;
                              // 새 탭에서 예약 상세 페이지 열기
                              window.open(`/reservation-detail/${orderNo}?saleNo=${nextSaleNo}&productName=${encodeURIComponent(productName || '')}`, '_blank');
                              handleClose();
                            }}
                          >
                            예약 상세
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 단체 정산 정보 */}
        <div>
          <div className="flex items-center justify-between mb-3 pb-2 pt-2 mt-10 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-1 h-5 bg-emerald-500 rounded-full"></div>
              <div className="text-sm font-semibold text-gray-800">단체 정산 정보</div>
            </div>
            <button
              type="button"
              onClick={() => setSettlementOpen(true)}
              disabled={loading || !groupProfit}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              정산서 생성
            </button>
          </div>
          <div className="pl-3 grid grid-cols-2 md:grid-cols-3 gap-3">
            <Tooltip title="클릭하여 행사 매출액 상세 보기" placement="top">
              <div
                role="button"
                tabIndex={0}
                className="group cursor-pointer hover:bg-blue-50 rounded-md p-2 -m-1 transition-colors"
                onClick={() => { setBreakdownType('revenue'); setBreakdownOpen(true); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setBreakdownType('revenue'); setBreakdownOpen(true); } }}
                aria-label="행사 매출액 내역 보기"
              >
                <div className="flex items-center gap-1">
                  <div className="text-[11px] text-gray-500">행사매출액</div>
                  <svg className="h-3.5 w-3.5 text-blue-400 group-hover:text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-base font-semibold tabular-nums text-blue-600">{loading ? '...' : fmtMoney(groupProfit?.행사매출액)}</div>
              </div>
            </Tooltip>

            <Tooltip title="클릭하여 행사 미수금 상세 보기" placement="top">
              <div
                role="button"
                tabIndex={0}
                className="group cursor-pointer hover:bg-amber-50 rounded-md p-2 -m-1 transition-colors"
                onClick={() => { setBreakdownType('receivable'); setBreakdownOpen(true); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setBreakdownType('receivable'); setBreakdownOpen(true); } }}
                aria-label="행사 미수금 내역 보기"
              >
                <div className="flex items-center gap-1">
                  <div className="text-[11px] text-gray-500">행사미수금</div>
                  <svg className="h-3.5 w-3.5 text-amber-400 group-hover:text-amber-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-base font-semibold tabular-nums text-amber-600">{loading ? '...' : fmtMoney(groupProfit?.행사미수금)}</div>
              </div>
            </Tooltip>

            <Tooltip title="클릭하여 행사 전도금(지출) 상세 보기" placement="top">
              <div
                role="button"
                tabIndex={0}
                className="group cursor-pointer hover:bg-gray-100 rounded-md p-2 -m-1 transition-colors"
                onClick={openPreDepositModal}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPreDepositModal(); } }}
                aria-label="행사 전도금(지출) 내역 보기"
              >
                <div className="flex items-center gap-1">
                  <div className="text-[11px] text-gray-500">행사전도금(지출)</div>
                  <svg className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-base font-semibold tabular-nums">{loading ? '...' : fmtMoney(groupProfit?.행사전도금)}</div>
              </div>
            </Tooltip>

            <Tooltip title="클릭하여 기타손익 추가/확인" placement="top">
              <div
                role="button"
                tabIndex={0}
                className="group cursor-pointer hover:bg-violet-50 rounded-md p-2 -m-1 transition-colors"
                onClick={openOtherIncomeModal}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOtherIncomeModal(); } }}
                aria-label="기타손익 내역 보기"
              >
                <div className="flex items-center gap-1">
                  <div className="text-[11px] text-gray-500">기타손익</div>
                  <svg className="h-3.5 w-3.5 text-violet-400 group-hover:text-violet-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-base font-semibold tabular-nums text-violet-600">{loading ? '...' : fmtMoney(groupProfit?.기타수익)}</div>
              </div>
            </Tooltip>

            <div className="p-2 -m-1">
              <div className="text-[11px] text-gray-500">행사예상수익</div>
              <div className="text-base font-semibold tabular-nums text-emerald-600">{loading ? '...' : fmtMoney(expectedProfitNum)}</div>
            </div>
          </div>

          <div className="pl-3 mt-3 flex flex-col gap-2">
            <div className="flex items-center gap-4 flex-wrap">
              <Checkbox
                checked={applyVatToProfit}
                onChange={(e) => setApplyVatToProfit(e.target.checked)}
                disabled={loading || !groupProfit}
              >
                <span className="text-xs text-gray-700">부가세 반영 (10%)</span>
              </Checkbox>
              <div className="flex items-center gap-2">
                <Tooltip title={isAssignedManager ? undefined : '수배담당자 본인만 확인할 수 있습니다.'}>
                  <Checkbox
                    checked={!!managerCheck?.확인여부}
                    disabled={managerCheckSaving || !isAssignedManager}
                    onChange={(e) => handleManagerCheckChange(e.target.checked)}
                  >
                    <span className={`text-xs ${managerCheck?.확인여부 ? 'text-blue-600 font-medium' : 'text-gray-700'}`}>
                      담당자 정산 확인
                    </span>
                  </Checkbox>
                </Tooltip>
                {managerCheck?.확인여부 && managerCheck?.확인일시 && (
                  <span className="text-[11px] text-gray-400">
                    {dayjs(managerCheck.확인일시).format('YYYY-MM-DD HH:mm')}
                  </span>
                )}
              </div>
            </div>
            {applyVatToProfit && (
              <div className="grid grid-cols-2 gap-3 rounded-md border border-emerald-100 bg-emerald-50/60 px-3 py-2.5">
                <div>
                  <div className="text-[11px] text-gray-500">예상 부가세</div>
                  <div className="text-sm font-semibold tabular-nums text-gray-800">
                    {loading ? '...' : fmtMoney(expectedVatNum)}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] text-gray-500">세후 예상수익</div>
                  <div className="text-sm font-semibold tabular-nums text-emerald-700">
                    {loading ? '...' : fmtMoney(afterTaxProfitNum)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Event info */}
        <div>
          <div className="flex items-center gap-2 mb-3 pb-2 mt-10 border-b border-gray-100">
            <div className="w-1 h-5 bg-indigo-500 rounded-full"></div>
            <div className="text-sm font-semibold text-gray-800">단체 상세 정보</div>
          </div>
          {loading && (
            <div className="text-gray-500 text-sm">행사 단체 정보를 불러오는 중...</div>
          )}
          {!loading && error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}
          {!loading && !error && (
            <div className="pl-3 space-y-2 text-sm">
              <div className="flex items-center justify-between py-1">
                <span className="text-gray-500">수배담당자명</span>
                <span className="font-medium">{groupProfit?.수배담당자명 || '-'}</span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-gray-500">행사기간</span>
                <span className="font-mono text-[13px] font-semibold">
                  {groupEvent?.시작일 || '-'} ~ {groupEvent?.종료일 || '-'}
                  {(() => {
                    const d = daysBetweenInclusive(groupEvent?.시작일, groupEvent?.종료일);
                    return d ? ` (${d}일)` : '';
                  })()}
                </span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-gray-500">정산상태</span>
                <div className="flex items-center gap-2">
                  <Switch
                    size="small"
                    checked={settlementClosed}
                    onChange={async (checked) => {
                      const prev = settlementClosed;
                      setSettlementClosed(checked);
                      if (!groupEvent?.단체번호) {
                        message.error('단체번호가 없어 저장할 수 없습니다.');
                        setSettlementClosed(prev);
                        return;
                      }
                      try {
                        await updateGroupSettlementStatus({
                          groupNo: groupEvent.단체번호,
                          settlementStatus: checked ? '1' : '0',
                        });
                        message.success('정산상태가 변경되었습니다.');
                        const refreshed = await getGroupEventInfo({ saleNo });
                        setGroupEvent(refreshed || null);
                        setSettlementClosed(refreshed?.정산상태 === '마감');
                      } catch (e) {
                        console.error(e);
                        message.error(e?.message || '정산상태 변경 중 오류가 발생했습니다.');
                        setSettlementClosed(prev);
                      }
                    }}
                  />
                  <span className={`text-xs ${settlementClosed ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>{settlementClosed ? '마감' : '미마감'}</span>
                </div>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-gray-500">출발확정여부</span>
                <div className="flex items-center gap-2">
                  <Switch
                    size="small"
                    checked={departureConfirm}
                    onChange={async (checked) => {
                      const prev = departureConfirm;
                      setDepartureConfirm(checked);
                      if (!groupEvent?.단체번호) {
                        message.error('단체번호가 없어 저장할 수 없습니다.');
                        setDepartureConfirm(prev);
                        return;
                      }
                      try {
                        const currentDisplay = !!groupEvent?.출발확정확정표시여부;
                        await saveGroupDepartureConfirm({
                          groupNo: groupEvent.단체번호,
                          departureConfirm: checked,
                          departureConfirmDisplay: currentDisplay,
                        });
                        message.success('출발확정 정보가 저장되었습니다.');
                        const refreshed = await getGroupEventInfo({ saleNo });
                        setGroupEvent(refreshed || null);
                        setDepartureConfirm(!!refreshed?.출발확정);
                        setScheduleConfirm(!!refreshed?.일정확정);
                      } catch (e) {
                        console.error(e);
                        message.error(e?.message || '저장 중 오류가 발생했습니다.');
                        setDepartureConfirm(prev);
                      }
                    }}
                  />
                  <span className={`text-xs ${departureConfirm ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>{departureConfirm ? '확정' : '미확정'}</span>
                </div>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-gray-500">일정확정여부</span>
                <div className="flex items-center gap-2">
                  <Switch
                    size="small"
                    checked={scheduleConfirm}
                    onChange={async (checked) => {
                      const prev = scheduleConfirm;
                      setScheduleConfirm(checked);
                      if (!groupEvent?.단체번호) {
                        message.error('단체번호가 없어 저장할 수 없습니다.');
                        setScheduleConfirm(prev);
                        return;
                      }
                      try {
                        const currentDisplay = !!groupEvent?.일정확정확정표시여부;
                        await saveGroupScheduleConfirm({
                          groupNo: groupEvent.단체번호,
                          scheduleConfirm: checked,
                          scheduleConfirmDisplay: currentDisplay,
                        });
                        message.success('일정확정 정보가 저장되었습니다.');
                        const refreshed = await getGroupEventInfo({ saleNo });
                        setGroupEvent(refreshed || null);
                        setScheduleConfirm(!!refreshed?.일정확정);
                        setDepartureConfirm(!!refreshed?.출발확정);
                      } catch (e) {
                        console.error(e);
                        message.error(e?.message || '저장 중 오류가 발생했습니다.');
                        setScheduleConfirm(prev);
                      }
                    }}
                  />
                  <span className={`text-xs ${scheduleConfirm ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>{scheduleConfirm ? '확정' : '미확정'}</span>
                </div>
              </div>

              <div className="mt-3 ">
                <div className="text-gray-500">첨부 화일</div>
                <div className="flex items-center gap-2">
                  {groupEvent?.첨부파일경로 ? (
                    <>
                      <div className="min-w-0 flex-1">
                        <a
                          className="block w-full px-2.5 py-1 rounded border border-blue-200 bg-white text-blue-700 hover:bg-blue-50 text-xs truncate"
                          href={`https://kr.object.iwinv.kr/orgin/${groupEvent.첨부파일경로}`}
                          target="_blank"
                          rel="noreferrer"
                          title={groupEvent?.첨부파일 || groupEvent?.첨부파일경로}
                        >
                          {groupEvent?.첨부파일 || (groupEvent?.첨부파일경로?.split('/').pop() || '다운로드')}
                        </a>
                      </div>
                      <button
                        className="shrink-0 inline-flex items-center px-2 py-1 rounded border border-red-200 bg-white text-red-600 hover:bg-red-50 text-xs"
                        onClick={handleDeleteAttachment}
                        disabled={deleting}
                        title="첨부파일 삭제"
                      >
                        {deleting ? '삭제 중...' : '삭제'}
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-xs text-gray-500">첨부 없음</span>
                      <button
                        type="button"
                        className="shrink-0 inline-flex items-center px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-xs"
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        disabled={uploading}
                        title="파일 선택"
                      >
                        {uploading ? '업로드 중...' : '파일 선택'}
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                          if (f) {
                            handleUpload(f);
                            e.target.value = '';
                          }
                        }}
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Remark subsection */}
              <div className="mt-4">
                <div className="flex items-center justify-between">
                  <div className="text-gray-500">단체 비고</div>
                  <button
                    type="button"
                    className="inline-flex items-center rounded border border-blue-200 bg-blue-400 text-white hover:bg-blue-700 disabled:opacity-60 px-2 py-1 text-xs"
                    onClick={handleSaveRemark}
                    disabled={remarkSaving}
                    title="비고 저장"
                  >
                    {remarkSaving ? '저장 중...' : '비고 저장'}
                  </button>
                </div>
                <textarea
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="비고를 입력하세요"
                />
              </div>
            </div>
          )}
        </div>

        

        {/* Breakdown Modal */}
        <Modal
          open={breakdownOpen}
          onCancel={() => setBreakdownOpen(false)}
          onOk={() => setBreakdownOpen(false)}
          width={880}
          title={breakdownType === 'receivable' ? '행사 미수금 내역' : '행사 매출액 내역'}
          okText="닫기"
          cancelButtonProps={{ style: { display: 'none' } }}
          bodyStyle={{ paddingTop: 8 }}
        >
          <div className="mb-2 text-sm text-gray-600">
            단체: <span className="font-medium">{resolvedTitle}</span>
          </div>
          {/* Quick totals badges */}
          <div className="flex flex-wrap gap-2 mb-3 text-xs">
            {(() => {
              const rows = Array.isArray(groupOrders) ? groupOrders : [];
              const sum = (arr, key) => arr.reduce((acc, cur) => acc + (Number(String(cur?.[key] ?? '').replace(/,/g, '')) || 0), 0);
              if (breakdownType === 'receivable') {
                const tPay = sum(rows, '고객결제가');
                const tDep = sum(rows, '입금액');
                const tDue = sum(rows, '미수액');
                return (
                  <>
                    <span className="inline-flex items-center px-2 py-1 rounded border border-amber-200 bg-amber-50 text-amber-700">고객결제가 합계: <span className="tabular-nums ml-1">{fmtMoney(tPay)}</span></span>
                    <span className="inline-flex items-center px-2 py-1 rounded border border-blue-200 bg-blue-50 text-blue-700">입금액 합계: <span className="tabular-nums ml-1">{fmtMoney(tDep)}</span></span>
                    <span className="inline-flex items-center px-2 py-1 rounded border border-red-200 bg-red-50 text-red-700">미수액 합계: <span className="tabular-nums ml-1">{fmtMoney(tDue)}</span></span>
                  </>
                );
              } else {
                const tProd = sum(rows, '상품금액');
                const tFuel = sum(rows, '택스및유류');
                const tCust = sum(rows, '고객결제가');
                return (
                  <>
                    <span className="inline-flex items-center px-2 py-1 rounded border border-gray-200 bg-gray-50 text-gray-700">상품금액 합계: <span className="tabular-nums ml-1">{fmtMoney(tProd)}</span></span>
                    <span className="inline-flex items-center px-2 py-1 rounded border border-purple-200 bg-purple-50 text-purple-700">택스및유류 합계: <span className="tabular-nums ml-1">{fmtMoney(tFuel)}</span></span>
                    <span className="inline-flex items-center px-2 py-1 rounded border border-gray-200 bg-gray-50 text-gray-700">고객결제가 합계: <span className="tabular-nums ml-1">{fmtMoney(tCust)}</span></span>
                  </>
                );
              }
            })()}
          </div>
          <Table
            size="small"
            pagination={false}
            rowKey={(row, idx) => String(row?.주문번호 || idx)}
            bordered
            columns={
              breakdownType === 'receivable'
                ? [
                  { title: <div style={{ textAlign: 'center' }}>주문번호</div>, dataIndex: '주문번호', key: '주문번호', width: 120, align: 'center', onHeaderCell: () => ({ style: { textAlign: 'center' } }) },
                  { title: <div style={{ textAlign: 'center' }}>주문자</div>, dataIndex: '주문자', key: '주문자', width: 160, align: 'center', onHeaderCell: () => ({ style: { textAlign: 'center' } }) },
                  { title: <div style={{ textAlign: 'center' }}>고객결제가</div>, dataIndex: '고객결제가', key: '고객결제가', width: 140, align: 'center', onHeaderCell: () => ({ style: { textAlign: 'center' } }), render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span> },
                  { title: <div style={{ textAlign: 'center' }}>입금액</div>, dataIndex: '입금액', key: '입금액', width: 140, align: 'center', onHeaderCell: () => ({ style: { textAlign: 'center' } }), render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span> },
                  { title: <div style={{ textAlign: 'center' }}>미수액</div>, dataIndex: '미수액', key: '미수액', width: 140, align: 'center', onHeaderCell: () => ({ style: { textAlign: 'center' } }), render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span> },
                ]
                : [
                  { title: <div style={{ textAlign: 'center' }}>주문번호</div>, dataIndex: '주문번호', key: '주문번호', width: 120, align: 'center', onHeaderCell: () => ({ style: { textAlign: 'center' } }) },
                  { title: <div style={{ textAlign: 'center' }}>주문자</div>, dataIndex: '주문자', key: '주문자', width: 160, align: 'center', onHeaderCell: () => ({ style: { textAlign: 'center' } }) },
                  { title: <div style={{ textAlign: 'center' }}>상품금액</div>, dataIndex: '상품금액', key: '상품금액', width: 140, align: 'center', onHeaderCell: () => ({ style: { textAlign: 'center' } }), render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span> },
                  { title: <div style={{ textAlign: 'center' }}>택스및유류</div>, dataIndex: '택스및유류', key: '택스및유류', width: 140, align: 'center', onHeaderCell: () => ({ style: { textAlign: 'center' } }), render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span> },
                  { title: <div style={{ textAlign: 'center' }}>고객결제가</div>, dataIndex: '고객결제가', key: '고객결제가', width: 140, align: 'center', onHeaderCell: () => ({ style: { textAlign: 'center' } }), render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span> },
                ]
            }
            dataSource={Array.isArray(groupOrders) ? groupOrders : []}
            scroll={{ x: 700, ...(shouldScrollYModal ? { y: 360 } : {}) }}
            onRow={(record) => ({
              onClick: () => {
                const orderNo = record?.주문번호;
                if (!orderNo) { message.warning('주문번호가 없습니다.'); return; }
                // Prefer the selected row's sale number if present, otherwise fall back to current saleNo
                const nextSaleNo = record?.판매번호 ?? record?.주문판매번호 ?? saleNo;
                const productName = record?.판매상품명 ?? record?.상품명 ?? undefined;
                // 새 탭에서 예약 상세 페이지 열기
                window.open(`/reservation-detail/${orderNo}?saleNo=${nextSaleNo}&productName=${encodeURIComponent(productName || '')}`, '_blank');
              },
            })}
            rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
            summary={(pageData) => {
              const sum = (arr, key) => arr.reduce((acc, cur) => acc + (Number(String(cur?.[key] ?? '').replace(/,/g, '')) || 0), 0);
              const rows = Array.isArray(pageData) ? pageData : [];
              const total상품 = sum(rows, '상품금액');
              const total유류 = sum(rows, '택스및유류');
              const total결제가 = sum(rows, '고객결제가');
              const total입금 = sum(rows, '입금액');
              const total미수 = sum(rows, '미수액');
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2} align="center">
                      <span className="font-medium">합계</span>
                    </Table.Summary.Cell>
                    {breakdownType === 'receivable' ? (
                      <>
                        <Table.Summary.Cell index={2} align="center"><span className="tabular-nums">{fmtMoney(total결제가)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="center"><span className="tabular-nums">{fmtMoney(total입금)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="center"><span className="tabular-nums">{fmtMoney(total미수)}</span></Table.Summary.Cell>
                      </>
                    ) : (
                      <>
                        <Table.Summary.Cell index={2} align="center"><span className="tabular-nums">{fmtMoney(total상품)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="center"><span className="tabular-nums">{fmtMoney(total유류)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="center"><span className="tabular-nums">{fmtMoney(total결제가)}</span></Table.Summary.Cell>
                      </>
                    )}
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Modal>
        <Modal
          open={preDepHelpOpen}
          onCancel={() => setPreDepHelpOpen(false)}
          footer={null}
          centered
          width={680}
          zIndex={2000}
          title="행사 전도금(지출) 내역 도움말"
        >
          <div className="space-y-4 text-xs text-gray-700">
            <div>
              <div className="font-semibold mb-1">1. 전도금(지출)이란?</div>
              <p className="mb-0">
                전도금(지출)은 단체 행사를 위해 미리 지출되는 비용(지상비, 항공료, 가이드비, 호텔비 등)을 의미합니다.
                전도금(지출)을 등록하면 정산서 작성 시 비용이 자동으로 반영되고, 행사예상수익 계산에도 포함됩니다.
              </p>
            </div>
            <div>
              <div className="font-semibold mb-1">2. 전도금(지출)을 등록하면 어떤 효과가 있나요?</div>
              <ul className="list-disc pl-4 space-y-1">
                <li>정산서에서 행사 비용인 전도금(지출)이 자동 집계되어 정산 작업이 간편해집니다.</li>
                <li>행사예상수익 = 행사매출액 - 행사미수금 - 행사전도금(지출) + 기타손익 으로 계산됩니다.</li>
                <li>부가세 반영 시 예상부가세 = 행사예상수익 ÷ 11, 세후예상수익 = 행사예상수익 - 예상부가세 입니다.</li>
                <li>전도금(지출)을 꾸준히 입력하면 행사별 실제 수익 구조를 한눈에 파악할 수 있습니다.</li>
              </ul>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2">
              <div className="font-semibold text-[11px]">3. 전도금(지출) 등록 방식 선택</div>
              <p className="mb-0">출납 내역에서 전도금(지출)을 전환하거나, "내역 추가"로 신규 출납과 함께 전도금(지출)을 등록할 수 있습니다.</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>
                  <span className="font-semibold">[신규 전도금(지출) 추가]</span>
                  <br />
                  새 출납(출금) 내역을 생성하면서 동시에 전도금(지출)을 등록합니다.
                  <br />
                  - 실제로 새로운 지출이 발생했을 때 사용합니다.
                  <br />
                  - 출금일, 지급방법, 출금 계좌, 송금 받는자, 지급액, 비고를 입력하면 출납 + 전도금(지출) + 결재 상신 + 출금반영까지 한 번에 처리됩니다.
                </li>
                <li>
                  <span className="font-semibold">[기존 출납에서 전도금(지출) 전환]</span>
                  <br />
                  이미 등록되어 있는 출납(출금) 내역의 잔액을 전도금(지출)으로 전환합니다.
                  <br />
                  - 카드 결제/계좌이체 등 출납이 먼저 잡혀 있는 경우 사용합니다.
                  <br />
                  - 하단 표에서 출납 내역을 선택하고, 전도금(지출)으로 사용할 금액을 입력한 뒤 전도금(지출)으로 전환하면, 해당 출납 잔액 범위 내에서 전도금(지출)이 생성되고 행사 비용에 반영됩니다.
                </li>
              </ul>
            </div>
            <div className="border-t pt-3 mt-2 text-[11px] text-gray-500 space-y-1">
              <div className="font-semibold">요약</div>
              <p className="mb-0">- 전도금(지출)은 단체 행사에 미리 사용하는 비용이며, 정산서와 예상수익 계산에 직접 영향을 줍니다.</p>
              <p className="mb-0">- 새로운 지출이면 "내역 추가"로 신규 출납+전도금(지출)을 등록하고, 이미 출납에 잡힌 금액이면 출납 내역을 선택해 전도금(지출)으로 전환하세요.</p>
            </div>
          </div>
        </Modal>
        {/* Pre-Deposit (행사 전도금) Modal */}
        <Modal
          open={preDepOpen}
          onCancel={() => setPreDepOpen(false)}
          onOk={() => setPreDepOpen(false)}
          width="min(1200px, calc(100vw - 32px))"
          title={(
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">행사 전도금(지출) 내역</span>
                  <Tooltip title="행사 전도금(지출)의 의미와 등록 방법 안내">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreDepHelpOpen(true);
                      }}
                      className="inline-flex items-center justify-center rounded-full border border-transparent text-xs text-sky-700 hover:text-sky-900 hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-1"
                      style={{ width: 22, height: 22 }}
                    >
                      <InfoCircleOutlined style={{ fontSize: 14 }} />
                    </button>
                  </Tooltip>
                </div>
                {preDepSummary && (
                  <div className="px-6 flex-1 text-[11px] text-gray-600 flex flex-wrap justify-end gap-x-4 gap-y-0.5">
                    <span>
                      단체: <span className="font-medium">{preDepSummary.행사단체명}</span>
                    </span>
                    <span>
                      수배담당: <span className="font-medium">{preDepSummary.수배담당}</span>
                    </span>
                    <span>
                      여행객: <span className="font-medium">{preDepSummary.여행객정보}</span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          okText="닫기"
          cancelButtonProps={{ style: { display: 'none' } }}
          bodyStyle={{ paddingTop: 8 }}
        >
          {preDepLoading && (
            <div className="text-gray-500 text-sm">전도금(지출) 정보를 불러오는 중...</div>
          )}
          {!preDepLoading && preDepError && (
            <div className="text-red-600 text-sm">{preDepError}</div>
          )}
          {!preDepLoading && !preDepError && (
            <>
              {/* 상단: 전도금 내역 카드 */}
              <div className="mb-3 rounded-xl border border-gray-200 bg-white px-4 py-3.5 shadow-sm">
                <div className="mb-1 flex items-center justify-between">
                  <div className="text-xs font-semibold text-gray-800">전도금(지출) 내역</div>
                </div>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="key"
                  rowClassName={() => 'group'}
                  columns={preDepListColumns}
                  dataSource={preDepTableData}
                  scroll={{ x: 1080, y: 360 }}
                />
              </div>

              {/* 출납 내역 + 내역 추가(신규 출납+전도금) */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="mb-1 flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold text-gray-800">출납 내역</div>
                    <div className="mt-0.5 text-[11px] text-gray-600">
                      아래 출납 리스트에서 건을 선택하면, 해당 잔액 범위 내에서 전도금(지출)으로 전환할 수 있습니다.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="inline-flex items-center rounded border border-blue-200 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 px-3 py-1.5 text-xs"
                    onClick={() => {
                      setShowAddPreDepForm((prev) => {
                        const next = !prev;
                        if (next) {
                          setPreDepPaymentMethod('무통장');
                          setPreDepSender('');
                          setPreDepDate(null);
                          setPreDepRemark('');
                          setPreDepAmount('');
                        }
                        return next;
                      });
                    }}
                  >
                    {showAddPreDepForm ? '취소' : '내역 추가'}
                  </button>
                </div>

                {showAddPreDepForm && (
                  <div className="mb-3 p-4 border border-blue-200 rounded-lg bg-blue-50">
                    <div className="text-sm font-semibold text-gray-800 mb-3">새 전도금(지출) 등록</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-[11px] font-medium text-gray-700">지급방법</span>
                          <select
                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs"
                            value={preDepPaymentMethod}
                            onChange={(e) => setPreDepPaymentMethod(e.target.value)}
                          >
                            <option value="무통장">무통장</option>
                            <option value="현금">현금</option>
                            <option value="일반카드">일반카드</option>
                          </select>
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-[11px] font-medium text-gray-700">출금일</span>
                          <DatePicker
                            size="small"
                            className="w-full py-1.5"
                            value={preDepDate}
                            onChange={(d) => setPreDepDate(d)}
                            format="YYYY-MM-DD"
                            placeholder="출납일 선택"
                          />
                        </div>
                      </div>

                      <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-[11px] font-medium text-gray-700">송금 받는자</span>
                          <Input
                            size="small"
                            className="w-full py-1.5"
                            placeholder="송금/지급자 이름"
                            value={preDepSender}
                            onChange={(e) => setPreDepSender(e.target.value)}
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-[11px] font-medium text-gray-700">출금 계좌</span>
                          <Select
                            size="small"
                            className="w-full"
                            value={preDepAccountId || undefined}
                            placeholder={bankAccountsLoading ? '계좌 불러오는 중...' : '계좌 선택'}
                            loading={bankAccountsLoading}
                            onChange={(v) => setPreDepAccountId(v)}
                            options={(bankAccounts || []).map((acc) => ({
                              value: String(acc.계좌일련번호 ?? ''),
                              label: `${acc.은행명 ?? ''} / ${acc.계좌번호 ?? ''}`,
                            }))}
                          />
                          {bankAccountsError && (
                            <span className="text-[11px] text-red-600">{bankAccountsError}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-medium text-gray-700">지급액</span>
                        <input
                          type="text"
                          className="w-40 rounded border border-gray-300 px-2 py-1 text-sm text-left tabular-nums disabled:bg-gray-100 disabled:text-gray-400"
                          placeholder={'지급액 입력'}
                          value={preDepAmount}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const cleaned = raw.replace(/[^0-9]/g, '');
                            if (!cleaned) {
                              setPreDepAmount('');
                              return;
                            }
                            let num = Number(cleaned);
                            if (!Number.isFinite(num)) {
                              setPreDepAmount(raw);
                              return;
                            }
                            setPreDepAmount(num.toLocaleString('ko-KR'));
                          }}
                        />
                      </div>

                      <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                        <span className="text-[11px] font-medium text-gray-700">비고</span>
                        <input
                          type="text"
                          className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                          placeholder="전도금(지출) 관련 메모를 입력하세요"
                          value={preDepRemark}
                          onChange={(e) => setPreDepRemark(e.target.value)}
                        />
                      </div>

                      <button
                        type="button"
                        className="inline-flex items-center rounded border border-blue-200 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 px-3 py-1.5 text-xs"
                        onClick={handleAddPreDeposit}
                        disabled={preDepSaving || preDepMappingSaving}
                      >
                        {preDepSaving ? '추가 중...' : '저장'}
                      </button>
                    </div>
                  </div>
                )}

                {cashbookLoading && (
                  <div className="text-gray-500 text-sm">출납 리스트를 불러오는 중...</div>
                )}
                {!cashbookLoading && cashbookError && (
                  <div className="text-red-600 text-sm">{cashbookError}</div>
                )}
                {!cashbookLoading && !cashbookError && (
                  <>
                    {renderCashbookRangeControls()}
                    <Table
                      size="small"
                      pagination={false}
                      rowKey="key"
                      rowSelection={{
                        type: 'radio',
                        selectedRowKeys: selectedCashbookTx ? [String(selectedCashbookTx)] : [],
                        onChange: (selectedRowKeys, selectedRows) => {
                          const row = Array.isArray(selectedRows) && selectedRows.length > 0 ? selectedRows[0] : null;
                          const tx = row?.거래일련번호;
                          setSelectedCashbookTx(tx || null);
                        },
                      }}
                      columns={cashbookListColumns}
                      dataSource={cashbookTableData}
                      scroll={{ x: 660, y: 260 }}
                    />

                    <div className="mt-2 flex flex-wrap items-center gap-3 pt-2 border-t border-dashed border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[11px] font-medium text-gray-700">지급액</span>
                          {selectedCashbookRow && (
                            <span className="text-[10px] text-gray-500">
                              선택 출납 잔액:{' '}
                              <span className="tabular-nums font-semibold text-gray-700">{fmtMoney(selectedCashbookRow.잔액)}</span>
                              <span className="ml-0.5">원</span>
                            </span>
                          )}
                        </div>
                        <input
                          type="text"
                          className="w-40 rounded border border-gray-300 px-2 py-1 text-sm text-left tabular-nums disabled:bg-gray-100 disabled:text-gray-400"
                          placeholder={
                            selectedCashbookRow
                              ? '전도금(지출)으로 전환할 금액'
                              : '출납 내역 선택'
                          }
                          value={preDepAmount}
                          disabled={!selectedCashbookRow}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const cleaned = raw.replace(/[^0-9]/g, '');
                            if (!cleaned) {
                              setPreDepAmount('');
                              return;
                            }
                            let num = Number(cleaned);
                            if (!Number.isFinite(num)) {
                              setPreDepAmount(raw);
                              return;
                            }
                            setPreDepAmount(num.toLocaleString('ko-KR'));
                          }}
                        />
                      </div>

                      <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                        <span className="text-[11px] font-medium text-gray-700">비고</span>
                        <input
                          type="text"
                          className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                          placeholder="전도금(지출) 관련 메모를 입력하세요"
                          value={preDepRemark}
                          onChange={(e) => setPreDepRemark(e.target.value)}
                        />
                      </div>

                      <button
                        type="button"
                        className="inline-flex items-center rounded border border-blue-200 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 px-3 py-1.5 text-xs"
                        onClick={handleAddPreDepositWithMapping}
                        disabled={preDepMappingSaving || !selectedCashbookTx}
                      >
                        {preDepMappingSaving ? '처리 중...' : '선택 출납에서 전도금(지출) 전환'}
                      </button>
                    </div>

                  </>
                )}
              </div>
            </>
          )}
        </Modal>
        {/* Other Income (기타손익) Modal */}
        <Modal
          open={otherIncomeOpen}
          onCancel={() => {
            setOtherIncomeOpen(false);
            resetOtherIncomeForm();
            resetOiDepositPanel();
          }}
          footer={null}
          width={960}
          destroyOnClose
          title={
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">기타손익 관리</span>
              <Tooltip
                title={
                  <div className="text-xs leading-relaxed space-y-1">
                    <p className="mb-0">기타손익은 매출/전도금 외로 단체에 잡히는 수익·손해 항목입니다.</p>
                    <p className="mb-0">등록하면 행사예상수익 = 매출액 - 미수금 - 전도금 + 기타손익 에 바로 반영됩니다.</p>
                    <p className="mb-0">플러스(+) 항목은 [입금]에서 수납(통장입금) 내역과 매핑할 수 있습니다.</p>
                    <p className="mb-0">마이너스(-) 항목은 출금 매핑이 불가하니, 실제 지출은 전도금(지출)으로 등록하세요.</p>
                  </div>
                }
              >
                <InfoCircleOutlined className="text-gray-400 text-sm" />
              </Tooltip>
            </div>
          }
        >
          {otherIncomeLoading && (
            <div className="text-gray-500 text-sm py-6 text-center">기타손익 정보를 불러오는 중...</div>
          )}
          {!otherIncomeLoading && otherIncomeError && (
            <div className="text-red-600 text-sm mb-3">{otherIncomeError}</div>
          )}
          {!otherIncomeLoading && (
            <div className="space-y-4">
              <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">기타손익 합계</div>
                  <div className="text-base font-semibold tabular-nums text-violet-600">
                    {fmtMoney(groupProfit?.기타수익)} 원
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-violet-100 bg-violet-50/40 p-3 space-y-3">
                <div className="text-xs font-semibold text-gray-800">
                  {editingOtherIncomeNo ? '기타손익 수정' : '기타손익 추가'}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-[11px] text-gray-500 mb-1">항목 *</div>
                    <Input
                      value={oiItem}
                      onChange={(e) => setOiItem(e.target.value)}
                      placeholder="예: 리베이트, 수수료 환급"
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <div className="text-[11px] text-gray-500 mb-1">담당자</div>
                    <Input
                      value={oiManager}
                      onChange={(e) => setOiManager(e.target.value)}
                      placeholder="담당자명"
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <div className="text-[11px] text-gray-500 mb-1">금액 (원) *</div>
                    <InputNumber
                      className="w-full"
                      value={oiAmount === '' ? null : Number(String(oiAmount).replace(/,/g, ''))}
                      onChange={(v) => setOiAmount(v == null ? '' : String(v))}
                      formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(v) => String(v || '').replace(/,/g, '')}
                      placeholder="0"
                    />
                    {oiFormAmountNum != null && oiFormAmountNum < 0 && (
                      <div className="mt-1.5 text-[11px] leading-relaxed text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
                        마이너스(손실) 항목은 출금 매핑이 불가합니다. 실제 지출은 <span className="font-medium">전도금(지출)</span>으로 등록해 주세요.
                      </div>
                    )}
                  </div>
                  <div className="flex items-end pb-1">
                    <Checkbox
                      checked={oiTaxInvoice}
                      onChange={(e) => setOiTaxInvoice(e.target.checked)}
                    >
                      <span className="text-xs text-gray-700">세금계산서 발행</span>
                    </Checkbox>
                  </div>
                </div>
                <div>
                  <div className="text-[11px] text-gray-500 mb-1">비고</div>
                  <Input.TextArea
                    rows={2}
                    value={oiRemark}
                    onChange={(e) => setOiRemark(e.target.value)}
                    placeholder="기타손익 관련 메모"
                    maxLength={1000}
                  />
                </div>
                <div className="flex items-center gap-2 justify-end">
                  {editingOtherIncomeNo != null && (
                    <button
                      type="button"
                      className="inline-flex items-center rounded border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
                      onClick={resetOtherIncomeForm}
                      disabled={otherIncomeSaving}
                    >
                      취소
                    </button>
                  )}
                  <button
                    type="button"
                    className="inline-flex items-center rounded border border-violet-200 bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60 px-3 py-1.5 text-xs"
                    onClick={handleSaveOtherIncome}
                    disabled={otherIncomeSaving}
                  >
                    {otherIncomeSaving ? '저장 중...' : (editingOtherIncomeNo ? '수정 저장' : '추가')}
                  </button>
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold text-gray-800 mb-2">등록된 기타손익</div>
                {(!otherIncomeRows || otherIncomeRows.length === 0) ? (
                  <div className="text-xs text-gray-500 py-4 text-center border border-dashed border-gray-200 rounded-md">
                    등록된 기타손익이 없습니다.
                  </div>
                ) : (
                  <Table
                    size="small"
                    pagination={false}
                    rowKey={(row) => String(row?.기타수익번호 ?? Math.random())}
                    dataSource={otherIncomeRows}
                    scroll={{ y: 200 }}
                    rowClassName={(row) => (
                      Number(row?.기타수익번호) === Number(oiDepositTargetNo)
                        ? 'bg-violet-50'
                        : ''
                    )}
                    columns={[
                      {
                        title: '항목',
                        dataIndex: '항목',
                        key: '항목',
                        ellipsis: true,
                      },
                      {
                        title: '담당자',
                        dataIndex: '담당자',
                        key: '담당자',
                        width: 90,
                        ellipsis: true,
                      },
                      {
                        title: '금액',
                        dataIndex: '한화환산',
                        key: '한화환산',
                        width: 110,
                        align: 'right',
                        render: (v, row) => (
                          <span className="tabular-nums">{fmtMoney(v ?? row?.금액)}</span>
                        ),
                      },
                      {
                        title: '입금',
                        key: '입금',
                        width: 200,
                        align: 'right',
                        render: (_, row) => {
                          const isNegative = getOtherIncomeAmount(row) < 0;
                          const isActive = Number(row?.기타수익번호) === Number(oiDepositTargetNo);
                          return (
                            <div className="flex items-center justify-end gap-1.5">
                              <span className="tabular-nums text-gray-800 min-w-[56px]">
                                {fmtMoney(row?.입금액)}
                              </span>
                              <button
                                type="button"
                                className={`px-1.5 py-0.5 text-[11px] rounded ${
                                  isActive
                                    ? (isNegative ? 'text-amber-800 bg-amber-100' : 'text-violet-700 bg-violet-100')
                                    : (isNegative ? 'text-amber-700 hover:bg-amber-50' : 'text-violet-600 hover:bg-violet-50')
                                }`}
                                onClick={() => openOiDepositPanel(row)}
                              >
                                {isNegative ? '안내' : '입금'}
                              </button>
                              <button
                                type="button"
                                className="px-1.5 py-0.5 text-[11px] text-blue-600 hover:bg-blue-50 rounded"
                                onClick={() => handleEditOtherIncome(row)}
                              >
                                수정
                              </button>
                              <button
                                type="button"
                                className="px-1.5 py-0.5 text-[11px] text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                                onClick={() => handleDeleteOtherIncome(row)}
                                disabled={otherIncomeDeletingId != null && Number(otherIncomeDeletingId) === Number(row?.기타수익번호)}
                              >
                                삭제
                              </button>
                            </div>
                          );
                        },
                      },
                    ]}
                  />
                )}
              </div>

              {oiDepositTargetNo != null && oiTargetIsNegative && (
                <div className="rounded-md border border-amber-200 bg-amber-50/70 overflow-hidden">
                  <div className="px-3 py-2.5 border-b border-amber-100 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-amber-900">
                        출금 매핑 안내
                        {oiDepositTargetRow?.항목 ? (
                          <span className="font-normal text-amber-800/80"> — {oiDepositTargetRow.항목}</span>
                        ) : null}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="shrink-0 px-2 py-1 text-[11px] text-amber-800/70 hover:bg-amber-100 rounded"
                      onClick={resetOiDepositPanel}
                    >
                      닫기
                    </button>
                  </div>
                  <div className="p-3 text-xs leading-relaxed text-amber-900 space-y-1.5">
                    <p className="mb-0">
                      이 항목은 마이너스(손실) 금액이라 <span className="font-medium">출금(출납) 매핑이 불가</span>합니다.
                    </p>
                    <p className="mb-0">
                      실제 현금 지출이 있으면 단체 정산의 <span className="font-medium">전도금(지출)</span>으로 등록·매핑해 주세요.
                    </p>
                    <p className="mb-0 text-amber-800/80">
                      기타손익 마이너스는 손익 반영용이며, 출금 증빙은 전도금 경로를 사용합니다.
                    </p>
                  </div>
                </div>
              )}

              {oiDepositTargetNo != null && !oiTargetIsNegative && (
                <div className="rounded-md border border-gray-200 overflow-hidden">
                  <div className="px-3 py-2.5 bg-gray-50 border-b flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-gray-800">
                        입금 매핑
                        {oiDepositTargetRow?.항목 ? (
                          <span className="font-normal text-gray-500"> — {oiDepositTargetRow.항목}</span>
                        ) : null}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-600">
                        <span>금액 <span className="tabular-nums font-medium text-gray-800">{fmtMoney(oiDepositTargetRow?.한화환산 ?? oiDepositTargetRow?.금액)}</span></span>
                        <span>입금 <span className="tabular-nums font-medium text-gray-800">{fmtMoney(oiDepositSummary?.입금액 ?? oiDepositTargetRow?.입금액)}</span></span>
                        <span>미수 <span className="tabular-nums font-medium text-violet-700">{fmtMoney(oiDepositDue)}</span></span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="shrink-0 px-2 py-1 text-[11px] text-gray-500 hover:bg-gray-100 rounded"
                      onClick={resetOiDepositPanel}
                    >
                      닫기
                    </button>
                  </div>

                  <div className="p-3 space-y-4">
                    <div>
                      <div className="text-xs font-semibold text-gray-800 mb-2">적용된 입금 내역</div>
                      {oiDepositLoading ? (
                        <div className="text-xs text-gray-500 py-3 text-center">불러오는 중...</div>
                      ) : (!oiDepositList || oiDepositList.length === 0) ? (
                        <div className="text-xs text-gray-500 py-3 text-center border border-dashed border-gray-200 rounded-md">
                          적용된 입금이 없습니다.
                        </div>
                      ) : (
                        <Table
                          size="small"
                          pagination={false}
                          rowKey={(row) => String(row?.결제번호 ?? Math.random())}
                          dataSource={oiDepositList}
                          scroll={{ y: 140 }}
                          columns={[
                            {
                              title: '수납일',
                              dataIndex: '수납일',
                              key: '수납일',
                              width: 150,
                              align: 'center',
                              render: (v) => (
                                <span className="whitespace-nowrap">{v || '-'}</span>
                              ),
                            },
                            {
                              title: '입금자',
                              dataIndex: '송금카드소유자',
                              key: '송금카드소유자',
                              ellipsis: true,
                              render: (v) => v || '-',
                            },
                            {
                              title: '수납액',
                              dataIndex: '수납액',
                              key: '수납액',
                              width: 100,
                              align: 'right',
                              render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
                            },
                            {
                              title: '적용액',
                              dataIndex: '입금액',
                              key: '입금액',
                              width: 100,
                              align: 'right',
                              render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
                            },
                            {
                              title: '',
                              key: 'del',
                              width: 60,
                              align: 'center',
                              render: (_, row) => (
                                <button
                                  type="button"
                                  className="px-1.5 py-0.5 text-[11px] text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                                  onClick={() => handleDeleteOiPayment(row)}
                                  disabled={oiDepositDeletingId != null && Number(oiDepositDeletingId) === Number(row?.결제번호)}
                                >
                                  삭제
                                </button>
                              ),
                            },
                          ]}
                        />
                      )}
                    </div>

                    <div>
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <div className="text-xs font-semibold text-gray-800">수납(통장입금) 내역</div>
                        <div className="flex items-center gap-1 flex-wrap">
                          {[
                            { key: 'today', label: '오늘' },
                            { key: '1w', label: '일주일' },
                            { key: '1m', label: '1개월' },
                            { key: '3m', label: '3개월' },
                            { key: 'custom', label: '기간' },
                          ].map((p) => (
                            <button
                              key={p.key}
                              type="button"
                              className={`px-2 py-0.5 text-[11px] rounded border ${
                                oiReceiptRangePreset === p.key
                                  ? 'border-violet-300 bg-violet-50 text-violet-700'
                                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                              }`}
                              onClick={() => applyOiReceiptRangePreset(p.key)}
                            >
                              {p.label}
                            </button>
                          ))}
                          {oiReceiptRangePreset === 'custom' && (
                            <DatePicker.RangePicker
                              size="small"
                              value={
                                oiReceiptStartDate && oiReceiptEndDate
                                  ? [dayjs(oiReceiptStartDate), dayjs(oiReceiptEndDate)]
                                  : null
                              }
                              onChange={async (dates) => {
                                if (!dates || dates.length !== 2) return;
                                const start = dates[0].format('YYYY-MM-DD');
                                const end = dates[1].format('YYYY-MM-DD');
                                setOiReceiptStartDate(start);
                                setOiReceiptEndDate(end);
                                await loadOiReceiptList({ startDate: start, endDate: end });
                              }}
                              format="YYYY-MM-DD"
                              allowClear={false}
                            />
                          )}
                        </div>
                      </div>

                      {oiReceiptLoading ? (
                        <div className="text-xs text-gray-500 py-3 text-center">수납 내역 불러오는 중...</div>
                      ) : (!oiReceiptList || oiReceiptList.length === 0) ? (
                        <div className="text-xs text-gray-500 py-3 text-center border border-dashed border-gray-200 rounded-md">
                          잔액이 있는 수납 내역이 없습니다.
                        </div>
                      ) : (
                        <Table
                          size="small"
                          pagination={false}
                          rowKey={(row) => String(row?.거래번호 ?? Math.random())}
                          dataSource={oiReceiptList}
                          scroll={{ y: 180, x: 640 }}
                          rowSelection={{
                            type: 'radio',
                            selectedRowKeys: oiSelectedReceiptTxNo ? [String(oiSelectedReceiptTxNo)] : [],
                            onChange: (keys, rows) => {
                              const row = rows?.[0];
                              const tx = row?.거래번호;
                              setOiSelectedReceiptTxNo(tx || null);
                              setOiSelectedReceiptRow(row || null);
                              const maxByBalance = Number(row?.잔액) || 0;
                              const maxAllowed = Math.max(0, Math.min(maxByBalance, oiDepositDue));
                              setOiApplyAmount(maxAllowed || null);
                            },
                          }}
                          columns={[
                            {
                              title: '수납일',
                              dataIndex: '수납일',
                              key: '수납일',
                              width: 100,
                              align: 'center',
                            },
                            {
                              title: '입금자',
                              dataIndex: '송급카드소유자',
                              key: '송급카드소유자',
                              width: 110,
                              ellipsis: true,
                              render: (v, row) => v || row?.송금카드소유자 || '-',
                            },
                            {
                              title: '수납액',
                              dataIndex: '수납액',
                              key: '수납액',
                              width: 100,
                              align: 'right',
                              render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
                            },
                            {
                              title: '잔액',
                              dataIndex: '잔액',
                              key: '잔액',
                              width: 100,
                              align: 'right',
                              render: (v) => <span className="tabular-nums">{fmtMoney(v)}</span>,
                            },
                            {
                              title: '계좌',
                              dataIndex: '계좌카드번호',
                              key: '계좌카드번호',
                              align: 'right',
                              ellipsis: true,
                              render: (v) => v || '-',
                            },
                          ]}
                        />
                      )}

                      {oiSelectedReceiptTxNo && (
                        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-md border border-violet-100 bg-violet-50/40 p-3">
                          <div>
                            <div className="text-[11px] text-gray-500 mb-1">적용 금액 *</div>
                            <InputNumber
                              size="small"
                              className="w-[160px]"
                              min={0}
                              value={oiApplyAmount}
                              onChange={(v) => setOiApplyAmount(v == null ? null : Number(v))}
                              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                              parser={(v) => String(v || '').replace(/,/g, '')}
                            />
                            <div className="mt-1 text-[10px] text-gray-500">
                              최대 {fmtMoney(Math.max(0, Math.min(Number(oiSelectedReceiptRow?.잔액) || 0, oiDepositDue)))} 원
                            </div>
                          </div>
                          <div className="flex-1 min-w-[160px]">
                            <div className="text-[11px] text-gray-500 mb-1">비고</div>
                            <Input
                              size="small"
                              value={oiApplyNote}
                              onChange={(e) => setOiApplyNote(e.target.value)}
                              placeholder="입금 적용 메모"
                              maxLength={500}
                            />
                          </div>
                          <button
                            type="button"
                            className="inline-flex items-center rounded border border-violet-200 bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60 px-3 py-1.5 text-xs"
                            onClick={handleApplyOiReceipt}
                            disabled={(() => {
                              const maxByBalance = Number(oiSelectedReceiptRow?.잔액) || 0;
                              const maxAllowed = Math.max(0, Math.min(maxByBalance, oiDepositDue));
                              const amt = Number(oiApplyAmount) || 0;
                              return oiApplying || amt <= 0 || amt > maxAllowed;
                            })()}
                          >
                            {oiApplying ? '적용 중...' : '입금 적용'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>
      </div>
    </Drawer>

    {renderPreDepRemarkModal()}

    {/* Settlement + email modals must sit outside Drawer stacking context */}
    <SettlementModal
      open={settlementOpen}
      onClose={() => setSettlementOpen(false)}
      loading={depositLoading || loading}
      recipientEmail={(
        Array.isArray(settlementRevenueRows)
          ? (settlementRevenueRows.find((r) => String(r?.주문자이메일 || '').trim())?.주문자이메일 || '')
          : ''
      )}
      settlementData={{
        groupName: (groupEvent?.행사단체명 || resolvedTitle || '').trim() || '-',
        groupNo: groupEvent?.단체번호,
        saleNo: saleNo,
        startDate: groupEvent?.시작일,
        endDate: groupEvent?.종료일,
        managerName: (
          groupProfit?.수배담당자명
          || groupProfit?.수배담당
          || groupEvent?.담당자명
          || groupEvent?.수배담당자명
          || ''
        ).toString().trim() || '-',
        revenue: groupProfit?.행사매출액,
        receivable: groupProfit?.행사미수금,
        preDeposit: groupProfit?.행사전도금,
        otherIncome: groupProfit?.기타수익,
        expectedProfit: expectedProfitNum,
        applyVat: applyVatToProfit,
        expectedVat: applyVatToProfit ? expectedVatNum : 0,
        afterTaxProfit: applyVatToProfit ? afterTaxProfitNum : expectedProfitNum,
        revenueBreakdown: settlementRevenueRows,
        receivableBreakdown: settlementReceivableRows,
        preDepositBreakdown: enrichedPreDepRows,
        otherIncomeBreakdown: otherIncomeRows,
        depositBreakdown: depositRows,
        remark: remark,
      }}
    />
    </>
  );
};

export default GroupInfoDrawer;
